import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { Button, Card, Col, Row, Table, Spinner, Toast, ToastContainer } from "react-bootstrap";
import { 
    projectDetail,
    projectEstimation,
    estimationRunCostSummaryJson,
    estimationRunMaterialSummary,
    estimationRunLabourSummary,
    estimationRunCostSummary,
    estimationRunMaterialSummaryJson,
    estimationRunLabourSummaryJson,
    clientDetail,
    projectRatesGet,
    projectRatesSave
} from "../../../shared/settings/apiConfig";
import { 
    BuildingIcon, 
    RupeeIcon,
    ImageIcon,
    ChartPieIcon,
    CategoriesIcon,
    AccuracyIcon
} from "../../../shared/icons/Icons";
import { getApi, postApi, putApi } from "../../../shared/services/apiHelper";
import { saveFileWithPrompt } from "../../../shared/utils/downloadUtils";
import CustomCostModal from "./CustomCostModal";

import './ProjectDetail.scss';

// Default material rates fallback (from backend material_rates.py)
const DEFAULT_MATERIAL_RATES = {
    "Cement": { "rate": 325, "unit": "bags (50 kg)" },
    "Cement (OPC 53)": { "rate": 350, "unit": "bags (50 kg)" },
    "Sand": { "rate": 1995.28, "unit": "cum" },
    "Sand (Zone II)": { "rate": 1995.28, "unit": "cum" },
    "Coarse Aggregate (20 mm)": { "rate": 1589.16, "unit": "cum" },
    "Coarse Aggregate (40 mm)": { "rate": 1589.16, "unit": "cum" },
    "Water": { "rate": 0.10, "unit": "litres" },
    "Water (compaction)": { "rate": 0.10, "unit": "litres" },
    "Admixture": { "rate": 180, "unit": "kg" },
    "TMT Steel Bars": { "rate": 72, "unit": "kg" },
    "Binding Wire": { "rate": 102, "unit": "kg" },
    "Door shutter": { "rate": 4900, "unit": "Nos" },
    "Wood Polish": { "rate": 330, "unit": "litres" },
    "Hardware Set (hinges/bolts)": { "rate": 450, "unit": "set" },
    "Nails / Screws": { "rate": 120, "unit": "kg" },
    "Timber (Seasoned Hardwood)": { "rate": 63566, "unit": "cum" },
    "UPVC Door Panel": { "rate": 4800, "unit": "sqm" },
    "Hardware Set": { "rate": 450, "unit": "set" },
    "Sealant": { "rate": 350, "unit": "litres" },
    "UPVC Frame & Sash": { "rate": 5200, "unit": "sqm" },
    "Glass (5 mm clear)": { "rate": 650, "unit": "sqm" },
    "Sealant / Gasket": { "rate": 350, "unit": "litres" },
    "UPVC Window": { "rate": 4800, "unit": "sqm" },
    "UPVC Frame": { "rate": 4800, "unit": "sqm" },
    "Mesh / Louvre": { "rate": 900, "unit": "sqm" },
    "Bricks (230x115x75 mm)": { "rate": 12.5, "unit": "nos" },
    "Floor Tiles (600x600 mm)": { "rate": 645.83, "unit": "sqm" },
    "Tile Grout": { "rate": 45, "unit": "kg" },
    "Primer (Wall)": { "rate": 130, "unit": "litres" },
    "Exterior Emulsion Paint": { "rate": 450, "unit": "litres" },
    "Interior Emulsion Paint": { "rate": 420, "unit": "litres" },
    "Wood Primer": { "rate": 230, "unit": "litres" },
    "Enamel / Wood Paint": { "rate": 320, "unit": "litres" },
    "Sandpaper": { "rate": 15, "unit": "sheet" },
    "Murrum / Gravel Fill": { "rate": 600, "unit": "cum" }
};

// Helper function to get rate with flexible key matching (case-insensitive, whitespace-tolerant)
const getRate = (rates, key, fallbackRates = DEFAULT_MATERIAL_RATES) => {
    if (!key) return 0;
    const normalizedKey = key.trim().toLowerCase();
    
    // Try to find rate in provided rates first
    if (rates && Object.keys(rates).length > 0) {
        // Try exact match first
        const exact = rates[key];
        if (exact?.rate != null && exact.rate !== 0) return exact.rate;
        
        // Try case-insensitive match
        for (const [k, v] of Object.entries(rates)) {
            if (k.toLowerCase() === normalizedKey && v?.rate != null && v.rate !== 0) return v.rate;
        }
        
        // Try whitespace-normalized match (remove extra spaces)
        const compactKey = normalizedKey.replace(/\s+/g, ' ');
        for (const [k, v] of Object.entries(rates)) {
            if (k.trim().toLowerCase().replace(/\s+/g, ' ') === compactKey && v?.rate != null && v.rate !== 0) return v.rate;
        }
        
        // Try partial match for common variations (e.g., "Cement" matches "Cement (OPC 53)")
        for (const [k, v] of Object.entries(rates)) {
            const lowerK = k.toLowerCase();
            if ((lowerK.includes(normalizedKey) || normalizedKey.includes(lowerK)) && v?.rate != null && v.rate !== 0) {
                // Prefer exact-ish match (e.g., "cement" matches "cement (opc 53)" but not vice versa)
                if (lowerK.startsWith(normalizedKey) || normalizedKey.startsWith(lowerK)) {
                    return v.rate;
                }
            }
        }
    }
    
    // Fallback to default rates if API returned 0 or empty
    if (fallbackRates && Object.keys(fallbackRates).length > 0) {
        // Try exact match in fallback
        const exactFallback = fallbackRates[key];
        if (exactFallback?.rate != null) return exactFallback.rate;
        
        // Try case-insensitive match in fallback
        for (const [k, v] of Object.entries(fallbackRates)) {
            if (k.toLowerCase() === normalizedKey && v?.rate != null) return v.rate;
        }
        
        // Try whitespace-normalized match in fallback
        const compactKey = normalizedKey.replace(/\s+/g, ' ');
        for (const [k, v] of Object.entries(fallbackRates)) {
            if (k.trim().toLowerCase().replace(/\s+/g, ' ') === compactKey && v?.rate != null) return v.rate;
        }
        
        // Try partial match in fallback
        for (const [k, v] of Object.entries(fallbackRates)) {
            const lowerK = k.toLowerCase();
            if ((lowerK.includes(normalizedKey) || normalizedKey.includes(lowerK)) && v?.rate != null) {
                if (lowerK.startsWith(normalizedKey) || normalizedKey.startsWith(lowerK)) {
                    return v.rate;
                }
            }
        }
    }
    
    return 0;
};

const getRateUnit = (rates, key, fallbackRates = DEFAULT_MATERIAL_RATES) => {
    if (!key) return "-";
    const normalizedKey = key.trim().toLowerCase();
    
    // Try to find unit in provided rates first
    if (rates && Object.keys(rates).length > 0) {
        // Try exact match first
        const exact = rates[key];
        if (exact?.unit) return exact.unit;
        
        // Try case-insensitive match
        for (const [k, v] of Object.entries(rates)) {
            if (k.toLowerCase() === normalizedKey && v?.unit) return v.unit;
        }
        
        // Try whitespace-normalized match
        const compactKey = normalizedKey.replace(/\s+/g, ' ');
        for (const [k, v] of Object.entries(rates)) {
            if (k.trim().toLowerCase().replace(/\s+/g, ' ') === compactKey && v?.unit) return v.unit;
        }
    }
    
    // Fallback to default rates for unit
    if (fallbackRates && Object.keys(fallbackRates).length > 0) {
        // Try exact match in fallback
        const exactFallback = fallbackRates[key];
        if (exactFallback?.unit) return exactFallback.unit;
        
        // Try case-insensitive match in fallback
        for (const [k, v] of Object.entries(fallbackRates)) {
            if (k.toLowerCase() === normalizedKey && v?.unit) return v.unit;
        }
        
        // Try whitespace-normalized match in fallback
        const compactKey = normalizedKey.replace(/\s+/g, ' ');
        for (const [k, v] of Object.entries(fallbackRates)) {
            if (k.trim().toLowerCase().replace(/\s+/g, ' ') === compactKey && v?.unit) return v.unit;
        }
    }
    
    return "-";
};

export default function ProjectDetail(props) {
    const projectId = props.match?.params?.id || props.projectId;

    const [loading, setLoading] = useState(true);
    const [projectData, setProjectData] = useState(null);
    const [activeTab, setActiveTab] = useState("summary");
    const [, setEstimation] = useState(null);
    const [allEstimations, setAllEstimations] = useState([]);
    const [cost, setCost] = useState(null);
    const [summary, setSummary] = useState(null);
    const [, setMaterialData] = useState(null);
    const [, setLabourData] = useState(null);
    const [, setShowDiagramModal] = useState(false);
    const [diagramModalLoading, setDiagramModalLoading] = useState(false);
    const [selectedDiagramImages, setSelectedDiagramImages] = useState([]);
    const [selectedDiagramName, setSelectedDiagramName] = useState("");
    const [, setSelectedDiagramEstimation] = useState(null);
    const [, setEstimationLoading] = useState(false);
    const [moduleItemsMap, setModuleItemsMap] = useState({}); // Map of module -> items array
    
    // New state for floor-based estimation tab
    const [floorData, setFloorData] = useState([]); // Array of floors with their modules
    const [selectedFloor, setSelectedFloor] = useState(null);
    const [selectedModule, setSelectedModule] = useState(null);
    const [selectedModuleEstimation, setSelectedModuleEstimation] = useState(null);
    const [selectedModuleCost, setSelectedModuleCost] = useState(null);
    
    // New state for floor-based material cost tab
    const [materialFloorData, setMaterialFloorData] = useState([]); // Array of floors with their modules
    const [materialFloorDataRaw, setMaterialFloorDataRaw] = useState([]); // Raw data without rate calculations
    const [selectedMaterialFloor, setSelectedMaterialFloor] = useState(null);
    const [selectedMaterialModule, setSelectedMaterialModule] = useState(null);
    const [selectedMaterialModuleData, setSelectedMaterialModuleData] = useState(null);
    const [materialRates, setMaterialRates] = useState({}); // Material rates: {materialName: {rate, unit}}
    const [materialRatesLoading, setMaterialRatesLoading] = useState(false);
    
    // New state for floor-based labour cost tab (matching material pattern)
    const [labourFloorData, setLabourFloorData] = useState([]); // Array of floors with their modules
    const [selectedLabourFloor, setSelectedLabourFloor] = useState(null);
    const [selectedLabourModule, setSelectedLabourModule] = useState(null);
    const [selectedLabourModuleData, setSelectedLabourModuleData] = useState(null);
    const [labourRates, setLabourRates] = useState({}); // Labour rates: {labourCategory: {rate, unit}}
    const [labourRatesLoading, setLabourRatesLoading] = useState(false);
    
    // Recalculate cost state
    const [, setRecalculatingCost] = useState(false);
    const [, setRecalculateSuccess] = useState(false);
    const [clientName, setClientName] = useState("");

    // Materials and Labour summary download states
    const [downloadingMaterial, setDownloadingMaterial] = useState(false);
    const [downloadingLabour, setDownloadingLabour] = useState(false);
    const [downloadingCost, setDownloadingCost] = useState(false);
    const [downloadMessage, setDownloadMessage] = useState(null);

    // Project Settings Modal state (consolidated: Module Rates, Material Rates, Labour Rates)
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [settingsActiveTab, setSettingsActiveTab] = useState('module'); // 'module' | 'material' | 'labour'
    const [settingsCustomRates, setSettingsCustomRates] = useState({});
    const [settingsRateInputStrings, setSettingsRateInputStrings] = useState({});
    const [settingsCustomCostSummary, setSettingsCustomCostSummary] = useState(null);
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [settingsError, setSettingsError] = useState(null);
    const [expandedFloor, setExpandedFloor] = useState(null);
    const [toast, setToast] = useState({ show: false, type: 'success', message: '' });

    const showToast = (message) => {
        setToast({ show: true, type: 'success', message });
    };

    // Material Settings state (for tab within modal)
    const [materialCustomRates, setMaterialCustomRates] = useState({});
    const [materialRateInputStrings, setMaterialRateInputStrings] = useState({});
    const [materialCustomSummary, setMaterialCustomSummary] = useState(null);
    const [materialSettingsSaving, setMaterialSettingsSaving] = useState(false);
    const [materialSettingsError, setMaterialSettingsError] = useState(null);
    const [expandedMaterialFloor, setExpandedMaterialFloor] = useState(null);

    // Labour Settings state (for tab within modal)
    const [labourCustomRates, setLabourCustomRates] = useState({});
    const [labourRateInputStrings, setLabourRateInputStrings] = useState({});
    const [labourCustomSummary, setLabourCustomSummary] = useState(null);
    const [labourSettingsSaving, setLabourSettingsSaving] = useState(false);
    const [labourSettingsError, setLabourSettingsError] = useState(null);
    const [expandedLabourFloor, setExpandedLabourFloor] = useState(null);

    // Tab display names mapping
    const tabDisplayNames = {
        overview: "Diagrams",
        estimation: "Estimation & Cost",
        module_cost: "Floor Wise Cost",
        material_cost: "Material Cost",
        labour_cost: "Labour Cost",
        summary: "Summary"
    };

    // Fetch project details from API
    useEffect(() => {
        if (projectId) {
            fetchProjectDetails();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const fetchProjectDetails = () => {
        setLoading(true);
        
        // Fetch project info first to get floor count
        getApi(`${projectDetail}/${projectId}`, true)
            .then(projectResponse => {
                const project = projectResponse.data;
                
                // Check if project is not completed - redirect to edit page
                // ProjectDetail is intended as read-only view for completed projects only
                if (project.status && project.status.toLowerCase() !== "completed") {
                    props.history.push(`/projects/edit/${projectId}`);
                    return Promise.reject({ redirected: true }); // Stop further execution
                }
                
                setProjectData(project);
                const floorsCount = project?.num_floors || project?.floors_count || project?.number_of_floors || 1;
                
                // Fetch client details (lightweight, separate from main data)
                if (project.client_id) {
                    getApi(`${clientDetail}/${project.client_id}`, true)
                        .then(clientRes => setClientName(clientRes.data?.name || project.client_id || ""))
                        .catch(() => setClientName(project.client_id || ""));
                }
                
                // Then fetch estimation, cost summary, material summary and labour summary in parallel
                return Promise.all([
                    fetchEstimation(),
                    fetchCostSummary(floorsCount),
                    fetchMaterialSummary(),
                    fetchLabourSummary(),
                    fetchMaterialRates(),
                    fetchLabourRates(),
                ]);
            })
            .then(([estimationData, summaryData, materialSummaryData, labourSummaryData]) => {
                setEstimation(estimationData);
                
                // Process summary data into cost & summary state
                if (summaryData) {
                    setCost(buildCostFromSummary(summaryData));
                    setSummary(buildSummaryFromData(summaryData));
                } else {
                    setCost(null);
                    setSummary(null);
                }
                
                // Process material and labour summaries
                if (materialSummaryData) {
                    setMaterialData(materialSummaryData);
                } else {
                    setMaterialData(null);
                }
                
                if (labourSummaryData) {
                    setLabourData(labourSummaryData);
                } else {
                    setLabourData(null);
                }
                
                setLoading(false);
            })
            .catch((error) => {
                // Don't log error if it's just a redirect
                if (!error.redirected) {
                    console.error("Error fetching project details:", error);
                }
                setLoading(false);
            });
    };

    const fetchEstimation = async () => {
        try {
            // Fetch ONLY completed estimations for this project
            // We use a large page size to get all records
            const response = await postApi(`${projectEstimation}list`, {
                filter: [
                    { field: "project_id", value: projectId, op: "=" },
                    { field: "status", value: "completed", op: "=" }
                ],
                page: { number: 1, size: 50 },
                sort: [{ field: "created_at", direction: "desc" }]
            }, true);
            
            if (response.data?.data?.length > 0) {
                const estimations = response.data.data;
                
                // Fetch full details (including items) for each estimation
                const fullEstimationPromises = estimations.map((est) => {
                    const estimationId = est.id || est._id;
                    return getApi(`${projectEstimation}${estimationId}`, true)
                        .then(res => res.data || est)
                        .catch((err) => {
                            console.error(`Error fetching estimation ${estimationId}:`, err);
                            return est;
                        });
                });
                
                const fullEstimations = await Promise.all(fullEstimationPromises);
                
                // Store all estimations with full details (including items)
                setAllEstimations(fullEstimations);
                
                // Get the most recent estimation for the estimation tab
                const latest = fullEstimations.reduce((prev, curr) => 
                    new Date(prev.created_at || 0) > new Date(curr.created_at || 0) ? prev : curr
                );
                return latest;
            }
            return null;
        } catch (error) {
            console.error("Error fetching estimation:", error);
            return null;
        }
    };

    const fetchCostSummary = async (floorsCount) => {
        try {
            // Call the cost summary JSON endpoint which returns combined cost data
            // This endpoint respects custom material/labour rates saved for the project
            // Let backend auto-discover floors from actual data in MongoDB
            const response = await postApi(estimationRunCostSummaryJson, {
                project_id: projectId
            }, true);
            if (response.data?.status === "success" && response.data?.rows?.length > 0) {
                // Extract items by module for diagram estimation tables
                extractModuleItems(response.data);
                return response.data;
            }
            return null;
        } catch (error) {
            console.error("Error fetching cost summary:", error);
            return null;
        }
    };

    /**
     * Fetch material summary from API
     */
    const fetchMaterialSummary = async () => {
        try {
            const response = await postApi(estimationRunMaterialSummaryJson, {
                project_id: projectId
            }, true);
            if (response.data?.status === "success" && response.data?.floors?.length > 0) {
                // Store raw hierarchical data for floor/module view
                const rawData = response.data;
                buildMaterialFloorStructure(rawData);
                return normalizeRequirementSummary(response.data, "material");
            }
            return null;
        } catch (error) {
            console.error("Error fetching material summary:", error);
            return null;
        }
    };

    /**
     * Convert the nested JSON requirement response into the row shape used by
     * the material/labour tabs. These endpoints return quantities, not prices.
     */
    const normalizeRequirementSummary = (data, type) => {
        const valueKey = type === "material" ? "materials" : "labours";
        const floorKey = type === "material" ? "materials" : "labour";
        const floors = (data.floors || []).map(floor => {
            const totals = {};
            (floor.modules || []).forEach(module => {
                (module.items || []).forEach(item => {
                    Object.entries(item[valueKey] || {}).forEach(([name, value]) => {
                        const quantity = Number(type === "material" ? value?.qty : value) || 0;
                        totals[name] = (totals[name] || 0) + quantity;
                    });
                });
            });
            return {
                floor_name: floor.floor_label,
                [floorKey]: Object.entries(totals).map(([name, quantity]) => ({
                    name,
                    [type === "material" ? "material_name" : "labour_category"]: name,
                    unit: type === "material"
                        ? (data.columns || []).find(column => column.key === name)?.unit || "-"
                        : "days",
                    quantity: Math.round(quantity * 10000) / 10000
                })),
                floor_total: totals
            };
        });
        return { ...data, floors, grand_total: data.grand_total || {} };
    };

    /**
     * Fetch labour summary from API
     */
    const fetchLabourSummary = async () => {
        try {
            const response = await postApi(estimationRunLabourSummaryJson, {
                project_id: projectId
            }, true);
            if (response.data?.status === "success" && response.data?.floors?.length > 0) {
                // Build hierarchical floor structure for new UI
                buildLabourFloorStructure(response.data);
                // Also return normalized data for backward compatibility
                return normalizeRequirementSummary(response.data, "labour");
            } else {
                setLabourFloorData([]);
            }
            return null;
        } catch (error) {
            console.error("Error fetching labour summary:", error);
            return null;
        }
    };

    /**
     * Fetch material rates for the project
     * Falls back to default rates if project-specific rates not found
     */
    const fetchMaterialRates = async () => {
        setMaterialRatesLoading(true);
        try {
            // Try project-specific rates first
            const response = await getApi(`/api/material-rates/${projectId}`, true);
            if (response.status === 200 && response.data?.rates && Object.keys(response.data.rates).length > 0) {
                setMaterialRates(response.data.rates);
                return;
            }
            
            // Fallback to default rates
            const defaultResponse = await getApi(`/api/material-rates/default`, true);
            if (defaultResponse.status === 200 && defaultResponse.data?.rates) {
                setMaterialRates(defaultResponse.data.rates);
            } else {
                console.warn("Default rates endpoint also returned empty data");
            }
        } catch (error) {
            console.error("Error fetching material rates:", error);
            // Final fallback: try default rates endpoint
            try {
                const defaultResponse = await getApi(`/api/material-rates/default`, true);
                if (defaultResponse.status === 200 && defaultResponse.data?.rates) {
                    setMaterialRates(defaultResponse.data.rates);
                }
            } catch (fallbackError) {
                console.error("Failed to fetch default rates as well:", fallbackError);
            }
        } finally {
            setMaterialRatesLoading(false);
        }
    };

    /**
     * Fetch labour rates for the project
     */
    const fetchLabourRates = async () => {
        setLabourRatesLoading(true);
        try {
            // Try project-specific rates first
            const response = await getApi(`/api/labour-rates/${projectId}`, true);
            if (response.status === 200 && response.data?.rates && Object.keys(response.data.rates).length > 0) {
                setLabourRates(response.data.rates);
                return;
            }
            
            // Fallback to default rates
            const defaultResponse = await getApi(`/api/labour-rates/default`, true);
            if (defaultResponse.status === 200 && defaultResponse.data?.rates) {
                setLabourRates(defaultResponse.data.rates);
            } else {
                console.warn("Default labour rates endpoint also returned empty data");
            }
        } catch (error) {
            console.error("Error fetching labour rates:", error);
            // Final fallback: try default rates endpoint
            try {
                const defaultResponse = await getApi(`/api/labour-rates/default`, true);
                if (defaultResponse.status === 200 && defaultResponse.data?.rates) {
                    setLabourRates(defaultResponse.data.rates);
                }
            } catch (fallbackError) {
                console.error("Failed to fetch default labour rates as well:", fallbackError);
            }
        } finally {
            setLabourRatesLoading(false);
        }
    };

    /**
     * Extract items from the rates summary and map them by module
     * Also maps rates-summary module names to estimation module keys
     * Captures floor information for each item
     */
    const extractModuleItems = (summaryData) => {
        if (!summaryData || !summaryData.rows) return;

        const rows = summaryData.rows;
        const moduleMap = {};
        let currentModule = "";
        let currentFloor = "";

        // Mapping from rates-summary module names to estimation module keys
        const moduleNameMapping = {
            "beams": "gfb",              // "Beams" in summary → "gfb" in estimations
            "floor_beam": "gfb",         // Alternative name
            "plinth_beam": "plinth_beam",
            "foundation": "foundation",
            "slab": "gfslab",
            "floor_slab": "gfslab",
            "columns": "column",
            "column": "column",
            "lintel_beam": "lb",
            "staircase": "staircase",
            "plan": "wfd",
            "elevation": "wfd"
        };

        rows.forEach((row) => {
            if (row.type === "floor") {
                // Capture the current floor context
                currentFloor = row["Floor / Module"] || "";
            } else if (row.type === "module") {
                currentModule = row["Floor / Module"] || "";
                // Normalize module name
                let normalizedModule = currentModule.toLowerCase().replace(/\s+/g, "_");
                
                // Map to estimation module key if mapping exists
                normalizedModule = moduleNameMapping[normalizedModule] || normalizedModule;
                
                if (!moduleMap[normalizedModule]) {
                    moduleMap[normalizedModule] = [];
                }
            } else if (row.type === "item" && currentModule) {
                let normalizedModule = currentModule.toLowerCase().replace(/\s+/g, "_");
                
                // Map to estimation module key if mapping exists
                normalizedModule = moduleNameMapping[normalizedModule] || normalizedModule;
                
                if (!moduleMap[normalizedModule]) {
                    moduleMap[normalizedModule] = [];
                }
                
                moduleMap[normalizedModule].push({
                    floor: currentFloor || "-",  // Add floor information
                    category: currentModule,
                    item: row["Item"],
                    description: row["Description"] || row["Item"], // Use item name as fallback
                    quantity: row["Quantity"],
                    unit: row["Unit"] || "-"
                });
            }
        });

        setModuleItemsMap(moduleMap);
        
        // Also build floor-based structure for the Estimation tab
        buildFloorStructure(rows, moduleNameMapping);
    };

    /**
     * Build a hierarchical floor structure for the Estimation tab
     * Organizes data as: Floor -> Modules -> Items
     */
    const buildFloorStructure = (rows, moduleNameMapping) => {
        const floorMap = new Map();
        let currentFloor = "";
        let currentModule = "";
        
        rows.forEach((row) => {
            if (row.type === "floor") {
                currentFloor = row["Floor / Module"] || "";
                if (currentFloor && !floorMap.has(currentFloor)) {
                    floorMap.set(currentFloor, {
                        name: currentFloor,
                        modules: new Map(),
                        totalCost: 0
                    });
                }
            } else if (row.type === "module" && currentFloor) {
                currentModule = row["Floor / Module"] || "";
                const floor = floorMap.get(currentFloor);
                if (floor && currentModule) {
                    // Normalize module name
                    let normalizedModule = currentModule.toLowerCase().replace(/\s+/g, "_");
                    normalizedModule = moduleNameMapping[normalizedModule] || normalizedModule;
                    
                    if (!floor.modules.has(normalizedModule)) {
                        floor.modules.set(normalizedModule, {
                            key: normalizedModule,
                            displayName: moduleDisplayNames[normalizedModule] || currentModule,
                            originalName: currentModule,
                            items: [],
                            totalCost: 0
                        });
                    }
                }
            } else if (row.type === "item" && currentFloor && currentModule) {
                const floor = floorMap.get(currentFloor);
                if (floor) {
                    let normalizedModule = currentModule.toLowerCase().replace(/\s+/g, "_");
                    normalizedModule = moduleNameMapping[normalizedModule] || normalizedModule;
                    
                    const module = floor.modules.get(normalizedModule);
                    if (module) {
                        const itemCost = row["Amount (₹)"] || 0;
                        module.items.push({
                            floor: currentFloor,
                            category: currentModule,
                            item: row["Item"],
                            description: row["Description"] || row["Item"],
                            quantity: row["Quantity"],
                            unit: row["Unit"] || "-",
                            rate: row["Rate (₹)"] || 0,
                            amount: itemCost
                        });
                        module.totalCost += itemCost;
                        floor.totalCost += itemCost;
                    }
                }
            }
        });

        // Convert Maps to Arrays for easier React rendering
        const floorArray = Array.from(floorMap.values()).map(floor => ({
            name: floor.name,
            totalCost: floor.totalCost,
            modules: Array.from(floor.modules.values())
        }));

        setFloorData(floorArray);
    };

    /**
     * Build a hierarchical floor structure for the Material Cost tab
     * Organizes data as: Floor -> Modules -> Materials with quantities
     */
    const buildMaterialFloorStructure = (rawData) => {
        if (!rawData || !rawData.floors) return;

        const floorArray = (rawData.floors || []).map(floor => {
            const modules = (floor.modules || []).map(module => {
                // Aggregate materials within this module
                const materialMap = new Map();
                
                (module.items || []).forEach(item => {
                    Object.entries(item.materials || {}).forEach(([name, value]) => {
                        // Safely extract quantity - handle both object {qty, unit} and direct number
                        const qty = typeof value === 'object' ? (Number(value?.qty) || 0) : (Number(value) || 0);
                        
                        // Skip if quantity is NaN or invalid
                        if (isNaN(qty)) {
                            console.warn(`Invalid quantity for material ${name}:`, value);
                            return;
                        }
                        
                        if (!materialMap.has(name)) {
                            materialMap.set(name, {
                                material_name: name,
                                unit: value?.unit || (rawData.columns?.find(c => c.key === name)?.unit) || "-",
                                quantity: 0
                            });
                        }
                        materialMap.get(name).quantity += qty;
                    });
                });

                const materials = Array.from(materialMap.values());
                const moduleTotalQty = materials.reduce((sum, m) => {
                    const q = Number(m.quantity) || 0;
                    return sum + (isNaN(q) ? 0 : q);
                }, 0);

                return {
                    key: module.label?.toLowerCase().replace(/\s+/g, "_") || "",
                    displayName: module.label || "Unknown Module",
                    originalName: module.label || "",
                    materials: materials,
                    totalQuantity: moduleTotalQty
                };
            });

            const floorTotalQty = modules.reduce((sum, m) => {
                const q = Number(m.totalQuantity) || 0;
                return sum + (isNaN(q) ? 0 : q);
            }, 0);

            return {
                name: floor.floor_label,
                modules: modules,
                totalQuantity: floorTotalQty
            };
        });

        // Store raw data for later enrichment with rates
        setMaterialFloorDataRaw(floorArray);
        setMaterialFloorData(floorArray);
    };

    const buildLabourFloorStructure = (rawData) => {
        if (!rawData || !rawData.floors) return;

        const floorArray = (rawData.floors || []).map(floor => {
            const modules = (floor.modules || []).map(module => {
                // Aggregate labour categories within this module
                const labourMap = new Map();
                
                (module.items || []).forEach(item => {
                    Object.entries(item.labours || {}).forEach(([name, quantity]) => {
                        const qty = Number(quantity) || 0;
                        
                        if (!labourMap.has(name)) {
                            labourMap.set(name, {
                                labour_category: name,
                                unit: rawData.columns?.find(c => c.key === name)?.unit || "days",
                                quantity: 0
                            });
                        }
                        labourMap.get(name).quantity += qty;
                    });
                });

                const labourCategories = Array.from(labourMap.values()).map(l => ({
                    ...l,
                    quantity: Math.round(l.quantity)
                }));
                const moduleTotalQty = labourCategories.reduce((sum, l) => sum + l.quantity, 0);

                return {
                    key: module.label?.toLowerCase().replace(/\s+/g, "_") || "",
                    displayName: module.label || "Unknown Module",
                    originalName: module.label || "",
                    labourCategories: labourCategories,
                    totalQuantity: moduleTotalQty
                };
            });

            const floorTotalQty = modules.reduce((sum, m) => sum + m.totalQuantity, 0);

            return {
                name: floor.floor_label,
                modules: modules,
                totalQuantity: floorTotalQty
            };
        });

        setLabourFloorData(floorArray);
    };

    /**
     * Transform the summary JSON response into the cost object expected by the cost tab.
     * The API returns rows with types: floor, module, item, module_subtotal, floor_total, grand_total
     */
    const buildCostFromSummary = (summaryData) => {
        if (!summaryData || !summaryData.rows) return null;

        const rows = summaryData.rows;
        const grandTotal = summaryData.meta?.grand_total || 0;

        // Walk through rows in order, tracking current floor/module for category context
        let currentFloor = "";
        let currentModule = "";
        const costItems = [];

        rows.forEach((row) => {
            if (row.type === "floor") {
                currentFloor = row["Floor / Module"] || "";
            } else if (row.type === "module") {
                currentModule = row["Floor / Module"] || "";
            } else if (row.type === "item") {
                costItems.push({
                    category: currentModule || currentFloor || "General",
                    item: row["Item"],
                    quantity: row["Quantity"],
                    rate: row["Rate (₹)"],
                    total: row["Amount (₹)"]
                });
            }
        });

        // Compute subtotal from item amounts (exclude null/zero-rate items)
        const subtotal = costItems.reduce((sum, item) => sum + (item.total || 0), 0);

        return {
            items: costItems,
            rows: rows,  // Include original rows array for Settings modal and other features
            subtotal: subtotal,
            tax_rate: 0,
            tax_amount: 0,
            grand_total: grandTotal,
            currency: "INR"
        };
    };

    /**
     * Transform the summary JSON response into the summary object expected by the summary tab.
     */
    const buildSummaryFromData = (summaryData) => {
        if (!summaryData || !summaryData.rows) return null;

        const rows = summaryData.rows;
        const grandTotal = summaryData.meta?.grand_total || 0;

        // Count items and group categories
        const itemRows = rows.filter(r => r.type === "item");
        const totalItems = itemRows.length;

        // Build categories by tracking module context
        let currentFloor = "";
        let currentModule = "";
        const categories = {};

        rows.forEach((row) => {
            if (row.type === "floor") {
                currentFloor = row["Floor / Module"] || "";
            } else if (row.type === "module") {
                currentModule = row["Floor / Module"] || "";
                if (currentModule) {
                    if (!categories[currentModule]) {
                        categories[currentModule] = { items: 0, total: 0 };
                    }
                }
            } else if (row.type === "item") {
                const catKey = currentModule || currentFloor || "General";
                if (!categories[catKey]) {
                    categories[catKey] = { items: 0, total: 0 };
                }
                categories[catKey].items += 1;
                categories[catKey].total += (row["Amount (₹)"] || 0);
            }
        });

        // Convert category values to plain numbers
        const flattenedCategories = {};
        Object.entries(categories).forEach(([key, val]) => {
            flattenedCategories[key] = { items: val.items, total: Math.round(val.total * 100) / 100 };
        });

        return {
            total_items: totalItems,
            grand_total: grandTotal,
            categories: flattenedCategories,
            summary_text: summaryData.meta?.generated 
                ? `Cost summary generated on ${summaryData.meta.generated}. Grand total: ₹${grandTotal.toLocaleString('en-IN')}. Covers ${totalItems} line items across ${Object.keys(flattenedCategories).length} categories.`
                : "Complete estimation for construction project with detailed material and labor breakdown."
        };
    };

    // Recalculate cost summary with latest rates
    const refetchCostSummary = async () => {
        setRecalculatingCost(true);
        setRecalculateSuccess(false);
        try {
            // Let backend auto-discover floors from actual data in MongoDB
            // This ensures resumed projects show all uploaded floors correctly
            const response = await postApi(estimationRunCostSummaryJson, {
                project_id: projectId
            }, true);
            
            if (response.data?.status === "success" && response.data?.rows?.length > 0) {
                // Extract items by module for diagram estimation tables
                extractModuleItems(response.data);
                
                // Update cost and summary state with fresh data
                setCost(buildCostFromSummary(response.data));
                setSummary(buildSummaryFromData(response.data));
                
                // Show success message
                setRecalculateSuccess(true);
                setTimeout(() => setRecalculateSuccess(false), 3000);
            }
        } catch (error) {
            console.error("Error recalculating cost:", error);
            alert("Failed to recalculate cost. Please try again.");
        } finally {
            setRecalculatingCost(false);
        }
    };

    // ============ Project Settings Modal Helpers ============

    /**
     * Recalculate cost summary with custom rates (copied from ProjectOnboard)
     * Modifies item rates and recomputes amounts, subtotals, floor totals, grand total
     */
    const recalcCostWithCustomRates = (originalSummary, ratesMap) => {
        if (!originalSummary || !originalSummary.rows) return originalSummary;

        const newRows = originalSummary.rows.map(row => {
            // Only modify actual data rows (type === 'item')
            if (row.type && row.type !== 'item') {
                // Keep floor/module banners, subtotals, totals as-is
                return { ...row };
            }

            const newRow = { ...row };
            const rateKey = row.Item || '';

            if (ratesMap[rateKey] !== undefined && ratesMap[rateKey] !== null) {
                const customRate = Number(ratesMap[rateKey]);
                newRow["Rate (₹)"] = customRate;
                const qty = Number(row.Quantity) || 0;
                newRow["Amount (₹)"] = qty * customRate;
            }
            return newRow;
        });

        // Recalculate subtotals, floor totals, grand total
        let currentModuleItems = [];
        let currentFloorItems = [];
        let grandTotal = 0;
        const recalculated = [];
        let currentModuleName = '';
        let currentFloorName = '';

        for (const row of newRows) {
            if (row.type === 'floor') {
                // Flush floor totals
                if (currentFloorItems.length > 0 && currentFloorName) {
                    const floorSum = currentFloorItems.reduce((sum, r) => sum + (Number(r["Amount (₹)"]) || 0), 0);
                    recalculated.push({ Item: "Floor Total", type: "floor_total", "Amount (₹)": floorSum });
                }
                currentFloorName = row["Floor / Module"];
                currentFloorItems = [];
                recalculated.push({ ...row });
            } else if (row.type === 'module') {
                // Flush previous module subtotal
                if (currentModuleItems.length > 0 && currentModuleName) {
                    const moduleSum = currentModuleItems.reduce((sum, r) => sum + (Number(r["Amount (₹)"]) || 0), 0);
                    recalculated.push({ Item: "Sub Total", type: "module_subtotal", "Amount (₹)": moduleSum });
                }
                currentModuleName = row["Floor / Module"];
                currentModuleItems = [];
                recalculated.push({ ...row });
            } else if (row.type === 'module_subtotal' || row.type === 'floor_total' || row.type === 'grand_total') {
                // Skip original totals - we'll recalculate
                continue;
            } else {
                recalculated.push(row);
                currentModuleItems.push(row);
                currentFloorItems.push(row);
            }
        }

        // Flush last module subtotal
        if (currentModuleItems.length > 0 && currentModuleName) {
            const moduleSum = currentModuleItems.reduce((sum, r) => sum + (Number(r["Amount (₹)"]) || 0), 0);
            recalculated.push({ Item: "Sub Total", type: "module_subtotal", "Amount (₹)": moduleSum });
        }

        // Flush last floor total
        if (currentFloorItems.length > 0 && currentFloorName) {
            const floorSum = currentFloorItems.reduce((sum, r) => sum + (Number(r["Amount (₹)"]) || 0), 0);
            recalculated.push({ Item: "Floor Total", type: "floor_total", "Amount (₹)": floorSum });
        }

        // Grand total
        grandTotal = recalculated
            .filter(r => r.type === 'floor_total')
            .reduce((sum, r) => sum + (Number(r["Amount (₹)"]) || 0), 0);
        recalculated.push({ Item: "Grand Total", type: "grand_total", "Amount (₹)": grandTotal });

        return { ...originalSummary, rows: recalculated };
    };

    /**
     * Initialize settings rates from current cost
     */
    const initializeSettingsRates = () => {
        // Guard: cost data must be loaded first
        if (!cost || !cost.rows || !Array.isArray(cost.rows)) {
            setSettingsError("Cost data is still loading. Please wait and try again.");
            return;
        }
        const rates = {};
        const inputStrs = {};
        cost.rows.forEach(row => {
            if (row.type === 'item' && row.Item && row["Rate (₹)"] !== undefined) {
                const rawRate = row["Rate (₹)"];
                const normalizedRate = (rawRate && typeof rawRate === 'object' && 'rate' in rawRate)
                    ? Number(rawRate.rate)
                    : Number(rawRate);
                rates[row.Item] = normalizedRate;
                inputStrs[row.Item] = String(normalizedRate);
            }
        });
        setSettingsCustomRates(rates);
        setSettingsRateInputStrings(inputStrs);
        // Trigger recalculation
        const updated = recalcCostWithCustomRates(cost, rates);
        setSettingsCustomCostSummary(updated);
    };

    /**
     * Safe error message extractor (from ProjectOnboard)
     */
    const safeErrorMessage = (error, fallback = "An unexpected error occurred.") => {
        if (!error) return fallback;
        const detail = error?.response?.data?.detail;
        if (!detail) return fallback;
        if (typeof detail === "string") return detail;
        if (Array.isArray(detail)) {
            return detail.map(err => err.msg || JSON.stringify(err)).join("; ");
        }
        if (typeof detail === "object" && detail !== null) {
            return detail.msg || detail.message || JSON.stringify(detail);
        }
        return String(detail);
    };

    /**
     * Handle rate change in settings modal
     */
    const handleSettingsRateChange = (itemName, rawValue) => {
        // Store the raw string input so user can type decimals properly
        setSettingsRateInputStrings(prev => ({
            ...prev,
            [itemName]: rawValue
        }));

        // Parse numeric value (allow empty string to clear)
        const numericRate = rawValue === '' ? 0 : parseFloat(rawValue);
        if (rawValue !== '' && isNaN(numericRate)) return; // Don't update if invalid

        // Update customRates; the useEffect below will recalculate customCostSummary automatically
        setSettingsCustomRates(prevRates => ({
            ...prevRates,
            [itemName]: isNaN(numericRate) ? 0 : numericRate
        }));
    };

    /**
     * Handle save settings - persist custom rates and refresh analysis
     */
    const handleSaveSettings = async () => {
        if (!projectId) return;
        setSettingsSaving(true);
        setSettingsError(null);
        try {
            // 1. Fetch existing rates from database first to preserve all rates
            const existingRatesResponse = await getApi(`${projectRatesGet}/${projectId}`);
            const existingRates = existingRatesResponse?.data?.rates || {};
            
            // 2. Normalize the edited rates from the modal, preserving unit field
            const normalizedEditedRates = {};
            Object.entries(settingsCustomRates).forEach(([key, value]) => {
                const numericRate = (value && typeof value === 'object' && 'rate' in value)
                    ? Number(value.rate)
                    : Number(value);
                
                // If existing rate has a unit field, preserve it as {rate, unit} object
                const existingRate = existingRates[key];
                if (existingRate && typeof existingRate === 'object' && 'unit' in existingRate) {
                    normalizedEditedRates[key] = { rate: numericRate, unit: existingRate.unit };
                } else {
                    normalizedEditedRates[key] = numericRate;
                }
            });
            
            // 3. Merge: start with existing rates, overlay edited ones
            // This preserves rates not visible in the cost summary
            const mergedRates = { ...existingRates, ...normalizedEditedRates };

            const response = await putApi(`${projectRatesSave}/${projectId}`, { 
                rates: mergedRates 
            });
            if (response.status === 200) {
                if (showToast) showToast("Module rates saved successfully!");
                // Refresh all project analysis with new rates
                await refetchCostSummary();
                // Don't close modal here - onSaveAll handles closing after all saves
            }
        } catch (error) {
            setSettingsError(safeErrorMessage(error, "Failed to save module rates."));
        } finally {
            setSettingsSaving(false);
        }
    };

    /**
     * Open settings modal and initialize rates
     */
    const handleOpenSettings = () => {
        initializeSettingsRates();
        initializeMaterialSettingsRates();
        initializeLabourSettingsRates();
        setSettingsActiveTab('module');
        setShowSettingsModal(true);
        setSettingsError(null);
        setExpandedFloor(null);
    };

    /**
     * Close settings modal and reset state
     */
    const handleCloseSettings = () => {
        setShowSettingsModal(false);
        setSettingsActiveTab('module');
        setSettingsCustomRates({});
        setSettingsRateInputStrings({});
        setSettingsCustomCostSummary(null);
        setSettingsError(null);
        setExpandedFloor(null);
    };

    // Auto-initialize settings rates when cost data becomes available
    useEffect(() => {
        if (cost && cost.rows && Array.isArray(cost.rows) &&
            Object.keys(settingsCustomRates).length === 0) {
            initializeSettingsRates();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cost, settingsCustomRates]);

    // Auto-recalculate custom cost summary when rates change
    useEffect(() => {
        if (cost && Object.keys(settingsCustomRates).length > 0) {
            const updated = recalcCostWithCustomRates(cost, settingsCustomRates);
            setSettingsCustomCostSummary(updated);
        }
    }, [settingsCustomRates, cost]);

    // ============ Material Settings Modal Helpers ============

    /**
     * Recalculate material floor data with custom rates
     */
    const recalcMaterialWithCustomRates = (floorData, ratesMap) => {
        if (!floorData || floorData.length === 0) return floorData;

        return floorData.map(floor => {
            const updatedModules = floor.modules.map(module => {
                const updatedMaterials = module.materials.map(material => {
                    const customRate = ratesMap[material.material_name];
                    const rate = customRate !== undefined && customRate !== null 
                        ? Number(customRate) 
                        : getRate(materialRates, material.material_name);
                    
                    return {
                        ...material,
                        rate: rate,
                        amount: material.quantity * rate
                    };
                });

                const moduleTotalAmount = updatedMaterials.reduce((sum, m) => sum + (m.amount || 0), 0);

                return {
                    ...module,
                    materials: updatedMaterials,
                    totalAmount: moduleTotalAmount
                };
            });

            const floorTotalAmount = updatedModules.reduce((sum, m) => sum + (m.totalAmount || 0), 0);

            return {
                ...floor,
                modules: updatedModules,
                totalAmount: floorTotalAmount
            };
        });
    };

    /**
     * Initialize material settings rates
     */
    const initializeMaterialSettingsRates = () => {
        // Use materialFloorDataRaw which has quantities but no rates yet
        // This ensures we always have data even if materialRates is still loading
        const sourceData = materialFloorDataRaw && materialFloorDataRaw.length > 0 
            ? materialFloorDataRaw 
            : materialFloorData;
            
        if (!sourceData || sourceData.length === 0) {
            setMaterialSettingsError("Material data is still loading. Please wait and try again.");
            return;
        }

        const rates = {};
        const inputStrs = {};

        sourceData.forEach(floor => {
            floor.modules.forEach(module => {
                module.materials.forEach(material => {
                    const name = material.material_name;
                    if (!rates[name]) {
                        // Use getRate helper with fallback to DEFAULT_MATERIAL_RATES
                        const currentRate = getRate(materialRates, name, DEFAULT_MATERIAL_RATES);
                        rates[name] = currentRate;
                        inputStrs[name] = String(currentRate);
                    }
                });
            });
        });

        setMaterialCustomRates(rates);
        setMaterialRateInputStrings(inputStrs);

        // Use sourceData for recalculation (has quantities, will apply rates)
        const updated = recalcMaterialWithCustomRates(sourceData, rates);
        setMaterialCustomSummary(updated);
    };

    /**
     * Handle material rate change
     */
    const handleMaterialRateChange = (materialName, rawValue) => {
        setMaterialRateInputStrings(prev => ({
            ...prev,
            [materialName]: rawValue
        }));

        const numericRate = rawValue === '' ? 0 : parseFloat(rawValue);
        if (rawValue !== '' && isNaN(numericRate)) return;

        setMaterialCustomRates(prevRates => ({
            ...prevRates,
            [materialName]: isNaN(numericRate) ? 0 : numericRate
        }));
    };

    /**
     * Handle save material settings
     */
    const handleSaveMaterialSettings = async () => {
        if (!projectId) return;
        setMaterialSettingsSaving(true);
        setMaterialSettingsError(null);
        try {
            const ratesPayload = {};
            Object.entries(materialCustomRates).forEach(([name, rate]) => {
                const unit = materialRates[name]?.unit || "-";
                ratesPayload[name] = { rate: Number(rate), unit: unit };
            });

            const response = await putApi(`/api/material-rates/${projectId}`, { 
                rates: ratesPayload 
            });
            
            if (response.status === 200) {
                if (showToast) showToast("Material rates saved successfully!");
                await Promise.all([
                    fetchMaterialSummary(),
                    fetchMaterialRates(),
                    refetchCostSummary()
                ]);
                // Don't close modal here - onSaveAll handles closing after all saves
            }
        } catch (error) {
            setMaterialSettingsError(safeErrorMessage(error, "Failed to save material rates."));
        } finally {
            setMaterialSettingsSaving(false);
        }
    };

// Auto-recalculate material summary when rates change
    useEffect(() => {
        if (materialFloorData && materialFloorData.length > 0 && Object.keys(materialCustomRates).length > 0) {
            const updated = recalcMaterialWithCustomRates(materialFloorData, materialCustomRates);
            setMaterialCustomSummary(updated);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [materialCustomRates, materialFloorData]);

    // Enrich materialFloorData with rates and amounts when materialRates are loaded
    useEffect(() => {
        if (materialFloorDataRaw && materialFloorDataRaw.length > 0 && materialRates && Object.keys(materialRates).length > 0) {
            const enrichedData = materialFloorDataRaw.map(floor => {
                const updatedModules = floor.modules.map(module => {
                    const updatedMaterials = module.materials.map(material => {
                        const rate = materialRates[material.material_name]?.rate || 0;
                        return {
                            ...material,
                            rate: rate,
                            amount: material.quantity * rate
                        };
                    });

                    const moduleTotalAmount = updatedMaterials.reduce((sum, m) => sum + (m.amount || 0), 0);

                    return {
                        ...module,
                        materials: updatedMaterials,
                        totalAmount: moduleTotalAmount
                    };
                });

                const floorTotalAmount = updatedModules.reduce((sum, m) => sum + (m.totalAmount || 0), 0);

                return {
                    ...floor,
                    modules: updatedModules,
                    totalAmount: floorTotalAmount
                };
            });

            setMaterialFloorData(enrichedData);
        }
    }, [materialRates, materialFloorDataRaw]);

    // ============ Labour Settings Modal Helpers ============

    /**
     * Recalculate labour floor data with custom rates
     */
    const recalcLabourWithCustomRates = (floorData, ratesMap) => {
        if (!floorData || floorData.length === 0) return floorData;

        return floorData.map(floor => {
            const updatedModules = floor.modules.map(module => {
                const updatedLabourCategories = module.labourCategories.map(labour => {
                    const customRate = ratesMap[labour.labour_category];
                    const rate = customRate !== undefined && customRate !== null 
                        ? Number(customRate) 
                        : (labourRates[labour.labour_category]?.rate || 0);
                    
                    const roundedQty = Math.round(labour.quantity);
                    
                    return {
                        ...labour,
                        rate: rate,
                        amount: roundedQty * rate
                    };
                });

                const moduleTotalAmount = updatedLabourCategories.reduce((sum, l) => sum + (l.amount || 0), 0);

                return {
                    ...module,
                    labourCategories: updatedLabourCategories,
                    totalAmount: moduleTotalAmount
                };
            });

            const floorTotalAmount = updatedModules.reduce((sum, m) => sum + (m.totalAmount || 0), 0);

            return {
                ...floor,
                modules: updatedModules,
                totalAmount: floorTotalAmount
            };
        });
    };

    /**
     * Initialize labour settings rates
     */
    const initializeLabourSettingsRates = () => {
        if (!labourFloorData || labourFloorData.length === 0) {
            setLabourSettingsError("Labour data is still loading. Please wait and try again.");
            return;
        }

        const rates = {};
        const inputStrs = {};

        labourFloorData.forEach(floor => {
            floor.modules.forEach(module => {
                module.labourCategories.forEach(labour => {
                    const category = labour.labour_category;
                    if (!rates[category] && labourRates[category]?.rate !== undefined) {
                        const currentRate = labourRates[category].rate;
                        rates[category] = currentRate;
                        inputStrs[category] = String(currentRate);
                    }
                });
            });
        });

        setLabourCustomRates(rates);
        setLabourRateInputStrings(inputStrs);

        const updated = recalcLabourWithCustomRates(labourFloorData, rates);
        setLabourCustomSummary(updated);
    };

    /**
     * Handle labour rate change
     */
    const handleLabourRateChange = (labourCategory, rawValue) => {
        setLabourRateInputStrings(prev => ({
            ...prev,
            [labourCategory]: rawValue
        }));

        const numericRate = rawValue === '' ? 0 : parseFloat(rawValue);
        if (rawValue !== '' && isNaN(numericRate)) return;

        setLabourCustomRates(prevRates => ({
            ...prevRates,
            [labourCategory]: isNaN(numericRate) ? 0 : numericRate
        }));
    };

    /**
     * Handle save labour settings
     */
    const handleSaveLabourSettings = async () => {
        if (!projectId) return;
        setLabourSettingsSaving(true);
        setLabourSettingsError(null);
        try {
            const ratesPayload = {};
            Object.entries(labourCustomRates).forEach(([category, rate]) => {
                const unit = labourRates[category]?.unit || "days";
                ratesPayload[category] = { rate: Number(rate), unit: unit };
            });

            const response = await putApi(`/api/labour-rates/${projectId}`, { 
                rates: ratesPayload 
            });
            
            if (response.status === 200) {
                if (showToast) showToast("Custom rates saved successfully!");
                await Promise.all([
                    fetchLabourSummary(),
                    fetchLabourRates(),
                    refetchCostSummary()
                ]);
                // Don't close modal here - onSaveAll handles closing after all saves
            }
        } catch (error) {
            setLabourSettingsError(safeErrorMessage(error, "Failed to save labour rates."));
        } finally {
            setLabourSettingsSaving(false);
        }
    };

// Auto-recalculate labour summary when rates change
    useEffect(() => {
        if (labourFloorData && labourFloorData.length > 0 && Object.keys(labourCustomRates).length > 0) {
            const updated = recalcLabourWithCustomRates(labourFloorData, labourCustomRates);
            setLabourCustomSummary(updated);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [labourCustomRates, labourFloorData]);

    // Handle Material Summary Download
    const handleMaterialsSummary = async () => {
        setDownloadingMaterial(true);
        setDownloadMessage(null);
        try {
            const projectName = (projectData?.project_name || projectData?.name || "Project").replace(/\s+/g, "_");

            // Build a blob promise without awaiting it yet, so the Save As
            // dialog opens immediately (within the user's click activation)
            // while the backend generates the Excel in the background.
            const blobPromise = (async () => {
                // Let backend auto-discover floors from actual data in MongoDB
                const response = await postApi(estimationRunMaterialSummary, {
                    project_id: projectId
                }, true);

                const excelUrl = response.data?.excel_url;
                if (!excelUrl) {
                    throw new Error("No Excel URL in response");
                }
                
                // Fetch the Excel file from the URL
                const res = await fetch(excelUrl);
                if (!res.ok) throw new Error(`HTTP ${res.status} while fetching ${excelUrl}`);
                return res.blob();
            })();

            const saved = await saveFileWithPrompt(blobPromise, `${projectName}_Material_Summary.xlsx`);

            // Only show success when the file was actually saved/initiated;
            // cancelling the Save As dialog resolves false and shows nothing.
            if (saved) {
                setDownloadMessage("Material summary downloaded successfully!");
                setTimeout(() => setDownloadMessage(null), 3000);
            }
        } catch (error) {
            console.error("Error downloading material summary:", error);
            alert("Failed to download material summary. Please try again.");
        } finally {
            setDownloadingMaterial(false);
        }
    };

    // Handle Labour Summary Download
    const handleLabourSummary = async () => {
        setDownloadingLabour(true);
        setDownloadMessage(null);
        try {
            const projectName = (projectData?.project_name || projectData?.name || "Project").replace(/\s+/g, "_");

            // Build a blob promise without awaiting it yet, so the Save As
            // dialog opens immediately (within the user's click activation)
            // while the backend generates the Excel in the background.
            const blobPromise = (async () => {
                // Let backend auto-discover floors from actual data in MongoDB
                const response = await postApi(estimationRunLabourSummary, {
                    project_id: projectId
                }, true);

                const excelUrl = response.data?.excel_url;
                if (!excelUrl) {
                    throw new Error("No Excel URL in response");
                }
                
                // Fetch the Excel file from the URL
                const res = await fetch(excelUrl);
                if (!res.ok) throw new Error(`HTTP ${res.status} while fetching ${excelUrl}`);
                return res.blob();
            })();

            const saved = await saveFileWithPrompt(blobPromise, `${projectName}_Labour_Summary.xlsx`);

            // Only show success when the file was actually saved/initiated;
            // cancelling the Save As dialog resolves false and shows nothing.
            if (saved) {
                setDownloadMessage("Labour summary downloaded successfully!");
                setTimeout(() => setDownloadMessage(null), 3000);
            }
        } catch (error) {
            console.error("Error downloading labour summary:", error);
            alert("Failed to download labour summary. Please try again.");
        } finally {
            setDownloadingLabour(false);
        }
    };

    // Handle Estimated Cost Summary Download (Project Rates)
    const handleEstimatedCostSummary = async () => {
        setDownloadingCost(true);
        setDownloadMessage(null);
        try {
            const projectName = (projectData?.project_name || projectData?.name || "Project").replace(/\s+/g, "_");

            // Build a blob promise without awaiting it yet, so the Save As
            // dialog opens immediately (within the user's click activation)
            // while the backend generates the Excel in the background.
            const blobPromise = (async () => {
                // Let backend auto-discover floors from actual data in MongoDB
                const response = await postApi(estimationRunCostSummary, {
                    project_id: projectId
                }, true);

                const excelUrl = response.data?.excel_url;
                if (!excelUrl) {
                    throw new Error("No Excel URL in response");
                }
                
                // Fetch the Excel file from the URL
                const res = await fetch(excelUrl);
                if (!res.ok) throw new Error(`HTTP ${res.status} while fetching ${excelUrl}`);
                return res.blob();
            })();

            const saved = await saveFileWithPrompt(blobPromise, `${projectName}_Estimated_Cost_Summary.xlsx`);

            // Only show success when the file was actually saved/initiated;
            // cancelling the Save As dialog resolves false and shows nothing.
            if (saved) {
                setDownloadMessage("Estimated cost summary downloaded successfully!");
                setTimeout(() => setDownloadMessage(null), 3000);
            }
        } catch (error) {
            console.error("Error downloading estimated cost summary:", error);
            alert("Failed to download estimated cost summary. Please try again.");
        } finally {
            setDownloadingCost(false);
        }
    };

    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null) return "₹0";
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 0
        }).format(amount);
    };

    // Exact value display — max 2 decimal places, no whole-rupee rounding
    const formatExactCurrency = (amount) => {
        if (amount === undefined || amount === null) return "₹0";
        const num = Number(amount);
        if (Number.isNaN(num)) return "₹0";
        return "₹" + num.toLocaleString("en-IN", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        });
    };

    // Helper function to detect if a URL is a PDF
    const isPdfUrl = (url) => {
        if (!url) return false;
        const urlLower = url.toLowerCase();
        return urlLower.endsWith('.pdf') || urlLower.includes('/documents/');
    };

    // Module display name mapping
    const moduleDisplayNames = {
        foundation: "Foundation",
        plinth_beam: "Plinth Beam",
        gfb: "Floor Beam",
        gfslab: "Floor Slab",
        column: "Columns",
        wfd: "Plan / Elevation",
        lb: "Lintel Beam",
        staircase: "Staircase"
    };

    // Extract unique diagram names from all estimations
    const getDiagramNames = () => {
        if (!allEstimations || allEstimations.length === 0) return [];
        // Filter only completed estimations for display
        const completedEstimations = allEstimations.filter(est => est.status === "completed");
        const names = completedEstimations.map(est => {
            const moduleKey = est.module || "";
            return moduleDisplayNames[moduleKey] || moduleKey.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
        });
        // Remove duplicates but preserve order
        return [...new Set(names)];
    };

    // Reverse lookup: get the module key from its display name
    const getModuleKeyFromName = (displayName) => {
        const entry = Object.entries(moduleDisplayNames).find(([, name]) => name === displayName);
        return entry ? entry[0] : null;
    };

    // Handle clicking on a module within a floor in the Estimation tab
    const handleModuleClick = (floor, module) => {
        setSelectedFloor(floor.name);
        setSelectedModule(module.key);
        
        // Set estimation data for this module
        setSelectedModuleEstimation({
            items: module.items,
            total_items: module.items.length,
            displayName: module.displayName,
            floorName: floor.name
        });
        
        // Set cost data for this module
        setSelectedModuleCost({
            items: module.items,
            subtotal: module.totalCost,
            grand_total: module.totalCost
        });
    };

    // Handle clicking on a material module badge
    const handleMaterialModuleClick = (floor, module) => {
        setSelectedMaterialFloor(floor.name);
        setSelectedMaterialModule(module.key);

        // Set material module data for display
        setSelectedMaterialModuleData({
            floorName: floor.name,
            moduleName: module.displayName,
            materials: module.materials
        });
    };

    // Handle clicking on a labour module badge
    const handleLabourModuleClick = (floor, module) => {
        setSelectedLabourFloor(floor.name);
        setSelectedLabourModule(module.key);

        // Set labour module data for display
        setSelectedLabourModuleData({
            floorName: floor.name,
            moduleName: module.displayName,
            labourCategories: module.labourCategories
        });
    };

    // Handle clicking on a diagram badge: fetch images for that module and use estimation data from allEstimations
    const handleDiagramClick = async (diagramName) => {
        setSelectedDiagramName(diagramName);
        setSelectedDiagramImages([]);
        setSelectedDiagramEstimation(null);
        setShowDiagramModal(true);
        setDiagramModalLoading(true);
        setEstimationLoading(true);

        // Find module key for this display name
        const moduleKey = getModuleKeyFromName(diagramName) || diagramName.toLowerCase().replace(/ /g, "_");

        // Find all estimations with this module
        const matchingEstimations = allEstimations.filter(est => {
            const estModule = est.module || "";
            return estModule === moduleKey;
        });

        if (matchingEstimations.length === 0) {
            setDiagramModalLoading(false);
            setEstimationLoading(false);
            return;
        }

        // Fetch images for each matching estimation
        const imagePromises = matchingEstimations
            .filter(est => est.id || est._id)
            .map(est => {
                const estimationId = est.id || est._id;
                return getApi(`${projectEstimation}${estimationId}`, true)
                    .then(res => res.data?.images || [])
                    .catch(() => []);
            });

        try {
            const imageResults = await Promise.all(imagePromises);
            const allImages = imageResults
                .flat()
                .filter(img => img && img.url)
                .map(img => img.url);

            setSelectedDiagramImages(allImages);

            // Get items from the moduleItemsMap (extracted from rates-summary-json)
            const moduleItems = moduleItemsMap[moduleKey] || [];
            const firstEstimation = matchingEstimations[0];
            
            if (moduleItems.length > 0) {
                const estimationData = {
                    items: moduleItems,
                    total_items: moduleItems.length,
                    ai_summary: firstEstimation.ai_summary || firstEstimation.summary || `Estimation details for ${diagramName}`,
                    module: firstEstimation.module
                };
                setSelectedDiagramEstimation(estimationData);
            } else {
                setSelectedDiagramEstimation({
                    items: [],
                    total_items: 0,
                    ai_summary: `Estimation details for ${diagramName}. No items available in the current estimation data.`,
                    module: firstEstimation.module
                });
            }
        } catch (error) {
            console.error("Error fetching diagram data:", error);
            setSelectedDiagramImages([]);
            setSelectedDiagramEstimation(null);
        } finally {
            setDiagramModalLoading(false);
            setEstimationLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center py-5 main-content">
                <div className="spinner-border text-purple" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!projectData) {
        return (
            <div className="text-center py-5 main-content">
                <h4 className="text-muted">Project not found</h4>
                <Button className="" onClick={() => props.history?.push("/home")}>
                    Back to Dashboard
                </Button>
            </div>
        );
    }

    const project = projectData;

    return (
        <div className="project-detail-container main-content animate-fade-in">
            

            {/* Header section with Actions */}
            <div className="project-header-section mb-4">
               

                {/* Single row: icon + name + meta | action buttons */}
                <div className="header-single-row">
                    <div className="d-flex align-items-center gap-3">

                         {/* Hamburger menu for mobile */}
                {/* <button className="d-lg-none border-0 bg-transparent d-flex align-items-center justify-content-center me-2" style={{ padding: 0 }} onClick={() => dispatch(actions.setSideBar(true))}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
                </button> */}

                 {/* Back link */}
                <div className="mb-3">
                    <button 
                        className="back-btn-circle" 
                        onClick={() => props.history?.push("/home")}
                        title="Back to Projects"
                    >
                        <i className="fa fa-arrow-left"></i>
                    </button>
                </div>
                        <div className="project-icon-circle">
                            <BuildingIcon color="#FFF" />
                        </div>
                        <div>
                            <h2 className="project-title mb-0">{project.name}</h2>
                            <div className="header-meta-info">
                                <span className="meta-date">
                                    <i className="fa fa-calendar mr-1"></i>
                                    {moment.utc(project.created_at).tz("Asia/Kolkata").format("DD MMMM YYYY")}
                                </span>
                                <span className="dot-separator">·</span>
                                <span className={`status-pill-badge status-${project.status}`}>
                                    {(project.status || "uploaded").toUpperCase()}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="header-actions-row">
                        <button 
                            className="action-pill-btn action-pill-btn-primary"
                            onClick={handleMaterialsSummary}
                            disabled={downloadingMaterial}
                        >
                            <i className={`fa ${downloadingMaterial ? 'fa-spinner fa-spin' : 'fa-cube'} mr-2`}></i>
                            Materials
                        </button>
                        <button 
                            className="action-pill-btn action-pill-btn-primary"
                            onClick={handleLabourSummary}
                            disabled={downloadingLabour}
                        >
                            <i className={`fa ${downloadingLabour ? 'fa-spinner fa-spin' : 'fa-users'} mr-2`}></i>
                            Labour
                        </button>
                        <button 
                            className="action-pill-btn action-pill-btn-primary"
                            onClick={handleEstimatedCostSummary}
                            disabled={downloadingCost}
                        >
                            <i className={`fa ${downloadingCost ? 'fa-spinner fa-spin' : 'fa-calculator'} mr-2`}></i>
                            Estimated Cost
                        </button>
                        <button 
                            className="action-pill-btn action-pill-btn-primary"
                            onClick={() => props.history?.push(`/projects/${projectId}/cpm`)}
                        >
                            <i className="fa fa-sitemap mr-2"></i>
                            CPM Plan
                        </button>
                        <button 
                            className="action-pill-btn action-pill-btn-secondary"
                            onClick={handleOpenSettings}
                            disabled={!cost || !cost.rows}
                            title={cost && cost.rows ? "Configure module rates and re-run analysis" : "Cost data loading..."}
                        >
                            <i className="fa fa-cogs mr-2"></i>
                            Project Settings
                        </button>
                    </div>
                </div>
            </div>

            {/* Download Success Message */}
            {downloadMessage && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                    <i className="fa fa-check-circle mr-2"></i>
                    {downloadMessage}
                </div>
            )}

            {/* Custom Tab Navigation Bar */}
            <div className="tab-navigation-bar mb-4">
                {["summary","overview", "estimation", "module_cost", "material_cost", "labour_cost"].map((tab) => (
                    <button
                        key={tab}
                        className={`tab-nav-item ${activeTab === tab ? "active" : ""}`}
                        onClick={() => setActiveTab(tab)}
                    >
                        <i className={`fa ${
                            tab === "summary" ? "fa-th-large" :
                            tab === "overview" ? "fa-image" :
                            tab === "estimation" ? "fa-clipboard-list" :
                            tab === "module_cost" ? "fa-layer-group" :
                            tab === "material_cost" ? "fa-cubes" :
                            "fa-users"
                        }`}></i>
                        <span className="tab-label-full">{tabDisplayNames[tab] || tab}</span>
                        <span className="tab-label-short">
                            {tab === "summary" ? "Summary" :
                             tab === "overview" ? "Diagrams" :
                             tab === "estimation" ? "Estimate" :
                             tab === "module_cost" ? "Floors" :
                             tab === "material_cost" ? "Material" :
                             "Labour"}
                        </span>
                    </button>
                ))}
            </div>

            {/* Tab Contents */}
            <div className="tab-content-wrapper">

                 {activeTab === "summary" && (
                    <div className="summary-tab-content">
                        {summary ? (
                            <>
                                {/* Summary Metric Cards with Wave Backgrounds */}
                                <Row className="g-4 mb-4">
                                    <Col lg={4} md={6}>
                                        <Card className="metric-card metric-card-purple">
                                            <div className="wave-background wave-purple"></div>
                                            <div className="metric-content">
                                                <div className="d-flex align-items-start justify-content-between">
                                                    <div>
                                                        <div className="metric-label d-flex align-items-center gap-2">
                                                            Grand Total 
                                                            
                                                        </div>
                                                        <div className="metric-value">{formatExactCurrency(summary.grand_total)}</div>
                                                    </div>
                                                    <div className="metric-icon metric-icon-purple">
                                                        <RupeeIcon color="#FFF" size="24" />
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                    <Col lg={4} md={6}>
                                        <Card className="metric-card metric-card-blue">
                                            <div className="wave-background wave-blue"></div>
                                            <div className="metric-content">
                                                <div className="d-flex align-items-start justify-content-between">
                                                    <div>
                                                        <div className="metric-label d-flex align-items-center gap-2">
                                                            Modules 
                                                             
                                                        </div>
                                                        <div className="metric-value">
                                                            {summary.categories ? Object.keys(summary.categories).length : 0}
                                                        </div>
                                                    </div>
                                                    <div className="metric-icon metric-icon-blue">
                                                        <CategoriesIcon color="#FFF" size="24" />
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                    <Col lg={4} md={6}>
                                        <Card className="metric-card metric-card-orange">
                                            <div className="wave-background wave-orange"></div>
                                            <div className="metric-content">
                                                <div className="d-flex align-items-start justify-content-between">
                                                    <div>
                                                        <div className="metric-label d-flex align-items-center gap-2">
                                                            Accuracy 
                                                            
                                                        </div>
                                                        <div className="metric-value">80% - 90%</div>
                                                    </div>
                                                    <div className="metric-icon metric-icon-orange">
                                                        <AccuracyIcon color="#FFF" size="24" />
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Project Overview and Recent Activity */}
                                <Row className="g-4">
                                    <Col lg={6}>
                                        <Card className="info-card overview-card">
                                            <Card.Body>
                                                <h5 className="section-title overview-title mb-4">
                                                    <span className="title-accent"></span>
                                                    Project Overview
                                                </h5>
                                                <div className="overview-item">
                                                    <div className="overview-left">
                                                        <div className="overview-icon">
                                                            <i className="fa fa-folder"></i>
                                                        </div>
                                                        <div className="overview-label">Project Name</div>
                                                    </div>
                                                    <div className="overview-value">
                                                        <div>{project.name}</div>
                                                     
                                                    </div>
                                                </div>

                                                 <div className="overview-item">
                                                    <div className="overview-left">
                                                        <div className="overview-icon">
                                                            <i className="fa fa-briefcase"></i>
                                                            
                                                        </div>
                                                        <div className="overview-label">Client:</div>
                                                    </div>
                                                    <div className="overview-value">
                                                        {clientName}
                                                    </div>
                                                </div>

                                                <div className="overview-item">
                                                    <div className="overview-left">
                                                        <div className="overview-icon">
                                                            <i className="fa fa-building"></i>
                                                        </div>
                                                        <div className="overview-label">Project Type</div>
                                                    </div>
                                                    <div className="overview-value">
                                                        {project.project_type 
                                                            ? (project.project_type.charAt(0).toUpperCase() + project.project_type.slice(1).toLowerCase())
                                                            : 'Not specified'}
                                                    </div>
                                                </div>


                                                <div className="overview-item">
                                                    <div className="overview-left">
                                                        <div className="overview-icon">
                                                            <i className="fa fa-calendar"></i>
                                                        </div>
                                                        <div className="overview-label">Created On</div>
                                                    </div>
                                                    <div className="overview-value">
                                                        {moment.utc(project.created_at).tz("Asia/Kolkata").format("DD MMMM YYYY [at] hh:mm a")}
                                                    </div>
                                                </div>
                                                <div className="overview-item">
                                                    <div className="overview-left">
                                                        <div className="overview-icon">
                                                            <i className="fa fa-flag"></i>
                                                        </div>
                                                        <div className="overview-label">Status</div>
                                                    </div>
                                                    <div className="overview-value">
                                                        <span className="status-pill-green">
                                                            <i className="fa fa-check-circle"></i>
                                                            {(project.status || "uploaded").charAt(0).toUpperCase() + (project.status || "uploaded").slice(1)}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="overview-item">
                                                    <div className="overview-left">
                                                        <div className="overview-icon">
                                                            <i className="fas fa-clock"></i> 
                                                        </div>
                                                        <div className="overview-label">Last Updated</div>
                                                    </div>
                                                    <div className="overview-value">
                                                        {moment.utc(project.updated_at || project.created_at).tz("Asia/Kolkata").format("DD MMMM YYYY [at] hh:mm a")}
                                                    </div>
                                                </div>
                                                <div className="overview-item">
                                                    <div className="overview-left">
                                                        <div className="overview-icon">
                                                            <i className="fa fa-user"></i>
                                                        </div>
                                                        <div className="overview-label">Created By</div>
                                                    </div>
                                                    <div className="overview-value">{project.created_by}</div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                  
                                </Row>
                            </>
                        ) : (
                            <Card className="info-card p-5 text-center">
                                <i className="fa fa-file-text-o fa-3x text-muted mb-3"></i>
                                <h5>No Summary Found</h5>
                                <p className="text-muted">Project summary is not generated yet.</p>
                            </Card>
                        )}
                    </div>
                )}

                {activeTab === "overview" && (
                    <div className="space-y-4">
                        <Row className="g-4">
                        

                            {/* Uploaded Diagrams Card */}
                            <Col lg={6}>
                                <Card className="activity-card p-4 h-100">
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                        <div className="p-2 rounded bg-light-green text-green">
                                            <ImageIcon color="#10b981" size="20" />
                                        </div>
                                        <h5 className="font-weight-bold mb-0">Uploaded Diagrams</h5>
                                    </div>
                                    
                                    {allEstimations.length > 0 ? (
                                        <div className="d-flex flex-wrap gap-2">
                                            {getDiagramNames().map((name, idx) => (
                                                <span 
                                                    key={idx} 
                                                    className={`status-badge capitalize px-3 py-2 ${selectedDiagramName === name ? 'status-inprogress' : 'status-valid'}`}
                                                    style={{ 
                                                        fontSize: "0.9rem", 
                                                        cursor: "pointer",
                                                        transition: "all 0.2s ease",
                                                        border: selectedDiagramName === name ? "2px solid #7e22ce" : "none"
                                                    }}
                                                    onClick={() => handleDiagramClick(name)}
                                                    onMouseEnter={(e) => {
                                                        if (selectedDiagramName !== name) {
                                                            e.currentTarget.style.transform = "translateY(-2px)";
                                                            e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1)";
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = "translateY(0)";
                                                        e.currentTarget.style.boxShadow = "none";
                                                    }}
                                                >
                                                    <i className={`fa ${selectedDiagramName === name ? 'fa-eye' : 'fa-check-circle'} ${selectedDiagramName === name ? 'text-purple' : 'text-success'} mr-2`}></i>
                                                    {name}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-center border rounded py-5 bg-light">
                                            <span className="text-muted">No diagrams uploaded</span>
                                        </div>
                                    )}
                                </Card>
                            </Col>

                                {/* Diagram Card */}
                            <Col lg={6}>
                                <Card className="activity-card p-4 h-100">
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                        <div className="p-2 rounded bg-light-blue text-blue">
                                            <ImageIcon color="#3b82f6" size="20" />
                                        </div>
                                        <h5 className="font-weight-bold mb-0">
                                            {selectedDiagramName ? `${selectedDiagramName} Diagrams` : 'Diagram'}
                                        </h5>
                                    </div>
                                    
                                    {diagramModalLoading ? (
                                        <div className="d-flex align-items-center justify-content-center border rounded py-5">
                                            <Spinner animation="border" variant="purple" />
                                            <span className="text-muted ms-3">Loading diagrams...</span>
                                        </div>
                                    ) : selectedDiagramName && selectedDiagramImages.length > 0 ? (
                                        <div className="diagram-images-container" style={{ maxHeight: "500px", overflowY: "auto" }}>
                                            <div className="d-flex flex-column gap-3">
                                                {selectedDiagramImages.map((imageUrl, idx) => {
                                                    const isPdf = isPdfUrl(imageUrl);
                                                    
                                                    if (isPdf) {
                                                        // Render PDF with iframe and Open button
                                                        return (
                                                            <div key={idx} className="border rounded overflow-hidden" style={{ position: "relative" }}>
                                                                <div style={{ 
                                                                    position: "absolute", 
                                                                    top: "10px", 
                                                                    right: "10px", 
                                                                    zIndex: 10 
                                                                }}>
                                                                    <Button
                                                                        variant="primary"
                                                                        size="sm"
                                                                        onClick={() => window.open(imageUrl, '_blank')}
                                                                        style={{
                                                                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                                                                        }}
                                                                    >
                                                                        <i className="fa fa-external-link mr-2"></i>
                                                                        Open in New Tab
                                                                    </Button>
                                                                </div>
                                                                <iframe
                                                                    src={imageUrl}
                                                                    title={`${selectedDiagramName} PDF diagram ${idx + 1}`}
                                                                    style={{ 
                                                                        width: "100%", 
                                                                        height: "600px", 
                                                                        border: "none"
                                                                    }}
                                                                />
                                                            </div>
                                                        );
                                                    } else {
                                                        // Render image as before
                                                        return (
                                                            <div key={idx} className="border rounded overflow-hidden p-">
                                                                <img
                                                                    src={imageUrl}
                                                                    alt={`${selectedDiagramName} diagram ${idx + 1}`}
                                                                    className="img-fluid rounded w-100"
                                                                    style={{ cursor: "zoom-in" }}
                                                                    onClick={() => window.open(imageUrl, '_blank')}
                                                                />
                                                            </div>
                                                        );
                                                    }
                                                })}
                                            </div>
                                        </div>
                                    ) : selectedDiagramName && selectedDiagramImages.length === 0 && !diagramModalLoading ? (
                                        <div className="d-flex flex-column align-items-center justify-content-center border rounded py-5" style={{  backgroundColor: "#0055ab !important" }}>
                                            <i className="fa fa-image fa-3x text-muted mb-2"></i>
                                            <span className="text-muted">No images found for {selectedDiagramName}</span>
                                        </div>
                                    ) : project.image_base64 ? (
                                        <div className="diagram-image-container border rounded overflow-hidden p-2" style={{  backgroundColor: "#0055ab !important" }}>
                                            <img
                                                src={`data:${project.mime_type};base64,${project.image_base64}`}
                                                alt="Construction diagram"
                                                className="img-fluid rounded w-100"
                                            />
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column align-items-center justify-content-center border rounded py-5" style={{  backgroundColor: "#0055ab !important" }}>
                                            <i className="fa fa-hand-pointer-o fa-3x text-muted mb-2"></i>
                                            <span className="text-muted">Click on a diagram name to view</span>
                                        </div>
                                    )}
                                </Card>
                            </Col>
                            
                        </Row>

     
                    </div>
                )}

                {activeTab === "estimation" && (
                    <div>
                        {floorData.length > 0 ? (
                            <div>
                                {/* Floor Count Header with Recalculate Button */}
                                <Card className="activity-card p-4 mb-4">
                                    <div className="d-flex align-items-center justify-content-between mb-3 tab-header-responsive">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="p-2 rounded bg-light-purple text-purple">
                                                <i className="fa fa-building fa-lg"></i>
                                            </div>
                                            <div>
                                                <h5 className="font-weight-bold mb-0">Module-wise Estimation</h5>
                                                <span className="text-muted text-sm">{floorData.length} floors in this project</span>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="text-right">
                                               <div className="text-muted" style={{ fontSize: '0.975rem' }}>Total Project Cost</div>
                                                <h4 className="font-weight-bold text-gradient mb-0">
                                                    {formatExactCurrency(floorData.reduce((sum, floor) => sum + floor.totalCost, 0))}
                                                </h4>
                                            </div>
                                            {/* <Button 
                                                className="secondary-btn-gradient fw-bold"
                                                onClick={refetchCostSummary}
                                                disabled={recalculatingCost}
                                            >
                                                <i className={`fa ${recalculatingCost ? 'fa-spinner fa-spin' : 'fa-refresh'} mr-2`}></i>
                                                {recalculatingCost ? 'Recalculating...' : 'Recalculate Cost'}
                                            </Button> */}
                                        </div>
                                    </div>
                                     
                                </Card>

                                {/* Floor Cards */}
                                {floorData.map((floor, floorIdx) => (
                                    <Card key={floorIdx} className="activity-card p-4 mb-4">
                                        <div className="d-flex align-items-center justify-content-between mb-3 tab-header-responsive">
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="p-2 rounded bg-light-blue text-blue">
                                                    <i className="fa fa-layer-group fa-lg"></i>
                                                </div>
                                                <div>
                                                    <h5 className="font-weight-bold mb-0">{floor.name}</h5>
                                                    <span className="text-muted text-sm">{floor.modules.length} modules</span>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-muted text-xs">Floor Cost</div>
                                                <h5 className="font-weight-bold text-dark mb-0">{formatExactCurrency(floor.totalCost)}</h5>
                                            </div>
                                        </div>

                                        {/* Module Badges */}
                                        <div className="d-flex flex-wrap gap-2">
                                            {floor.modules.map((module, moduleIdx) => (
                                                <span
                                                    key={moduleIdx}
                                                    className={`status-badge capitalize px-3 py-2 ${
                                                        selectedFloor === floor.name && selectedModule === module.key
                                                            ? 'status-inprogress'
                                                            : 'status-valid'
                                                    }`}
                                                    style={{
                                                        fontSize: "0.9rem",
                                                        cursor: "pointer",
                                                        transition: "all 0.2s ease",
                                                        border:
                                                            selectedFloor === floor.name && selectedModule === module.key
                                                                ? "2px solid #7e22ce"
                                                                : "none"
                                                    }}
                                                    onClick={() => handleModuleClick(floor, module)}
                                                    onMouseEnter={(e) => {
                                                        if (!(selectedFloor === floor.name && selectedModule === module.key)) {
                                                            e.currentTarget.style.transform = "translateY(-2px)";
                                                            e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1)";
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = "translateY(0)";
                                                        e.currentTarget.style.boxShadow = "none";
                                                    }}
                                                >
                                                    <i className={`fa ${selectedFloor === floor.name && selectedModule === module.key ? 'fa-eye' : 'fa-cube'} mr-2`}></i>
                                                    {module.displayName}
                                                    <span className="ms-2 text-xs">({module.items.length})</span>
                                                </span>
                                            ))}
                                        </div>
                                    </Card>
                                ))}

                                {/* Estimation Table for Selected Module */}
                                {selectedModuleEstimation && (
                                    <Card className="activity-card p-4 mb-4">
                                        <div className="d-flex align-items-center gap-2 mb-4">
                                            <div className="p-2 rounded bg-light-purple text-purple">
                                                <ChartPieIcon color="#7e22ce" size="20" />
                                            </div>
                                            <div>
                                                <h5 className="font-weight-bold mb-0">
                                                    {selectedModuleEstimation.floorName} - {selectedModuleEstimation.displayName}
                                                </h5>
                                                <span className="text-muted text-sm">
                                                    {selectedModuleEstimation.total_items} items estimated
                                                </span>
                                            </div>
                                        </div>

<div className="table-responsive">
                                            <Table hover className="custom-detail-table">
                                                <thead>
                                                    <tr>
                                                        <th className="sticky-col">Item</th>
                                                        <th className="text-right">Description</th>
                                                        <th className="text-right">Quantity</th>
                                                        <th>Unit</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedModuleEstimation.items.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td className="font-weight-bold text-dark sticky-col">{item.item}</td>
                                                            <td className="text-muted text-sm text-right">{item.description || '-'}</td>
                                                            <td className="text-right font-weight-bold text-dark">
                                                                {item.quantity?.toLocaleString() || '-'}
                                                            </td>
                                                            <td className="text-muted">{item.unit || '-'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Card>
                                )}

                                {/* Cost Table for Selected Module */}
                                {selectedModuleCost && (
                                    <Card className="activity-card p-4">
                                        <div className="d-flex align-items-center gap-2 mb-4">
                                            <div className="p-2 rounded bg-light-orange text-orange">
                                                <RupeeIcon color="#f97316" size="24" />
                                            </div>
                                            <div>
                                                <h5 className="font-weight-bold mb-0">Cost Breakdown</h5>
                                                <span className="text-muted text-sm">Detailed pricing for selected module</span>
                                            </div>
                                        </div>

                                        <div className="table-responsive mb-4">
                                            <Table hover className="custom-detail-table">
                                                <thead>
                                                    <tr>
                                                        <th className="sticky-col">Item</th>
                                                        <th className="text-right">Quantity</th>
                                                        <th className="text-right">Rate</th>
                                                        <th className="text-right">Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedModuleCost.items.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td className="font-weight-bold text-dark sticky-col">{item.item}</td>
                                                            <td className="text-right text-dark">
                                                                {item.quantity != null ? item.quantity.toLocaleString() : '-'}
                                                            </td>
                                                            <td className="text-right text-muted">
                                                                {item.rate != null ? formatExactCurrency(item.rate) : '-'}
                                                            </td>
                                                            <td className="text-right font-weight-bold text-dark">
                                                                {item.amount != null ? formatExactCurrency(item.amount) : '-'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>

                                        <div className="p-4 rounded border border-orange bg-light-orange-light ms-auto module-total-box">
                                            <div className="d-flex justify-content-between align-items-center">
                                               <span className="font-weight-bold text-dark" style={{ marginRight: '8px' , fontSize: '1.222rem' }}>Module Total</span>
<span className="font-weight-bold text-xl text-gradient">
    {formatExactCurrency(selectedModuleCost.grand_total)}
</span>
                                            </div>
                                        </div>
                                    </Card>
                                )}
                            </div>
                        ) : (
                            <Card className="activity-card p-4">
                                <div className="text-center py-5">
                                    <i className="fa fa-calculator fa-3x text-muted mb-3"></i>
                                    <h5>No Estimation Data Available</h5>
                                    <p className="text-muted text-sm">
                                        Floor-based estimation data will appear here once generated.
                                    </p>
                                </div>
                            </Card>
                        )}
                    </div>
                )}

                {activeTab === "module_cost" && (
                    <div>
                        {floorData.length > 0 ? (
                            <div>
                                {/* Module Cost Header with Recalculate Button */}
                                <Card className="activity-card p-4 mb-4">
                                    <div className="d-flex align-items-center justify-content-between mb-3 tab-header-responsive">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="p-2 rounded bg-light-orange text-orange">
                                                <i className="fa fa-cubes fa-lg"></i>
                                            </div>
                                            <div>
                                                <h5 className="font-weight-bold mb-0">Floor-wise Cost Breakdown</h5>
                                                <span className="text-muted text-sm">Detailed cost analysis per floor</span>
                                            </div>
                                        </div>
                                        {/* <Button 
                                            className="secondary-btn-gradient fw-bold"
                                            onClick={refetchCostSummary}
                                            disabled={recalculatingCost}
                                        >
                                            <i className={`fa ${recalculatingCost ? 'fa-spinner fa-spin' : 'fa-refresh'} mr-2`}></i>
                                            {recalculatingCost ? 'Recalculating...' : 'Recalculate Cost'}
                                        </Button> */}
                                    </div>
                                    
                                </Card>

                                {/* Floor-wise Cards with Module Cost Tables */}
                                {floorData.map((floor, floorIdx) => (
                                    <Card key={floorIdx} className="activity-card p-4 mb-4">
                                        <div className="d-flex align-items-center justify-content-between mb-3 tab-header-responsive">
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="p-2 rounded bg-light-blue text-blue">
                                                    <i className="fa fa-layer-group fa-lg"></i>
                                                </div>
                                                <div>
                                                    <h5 className="font-weight-bold mb-0">{floor.name}</h5>
                                                    <span className="text-muted text-sm">{floor.modules.length} modules</span>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-muted text-xs">Floor Cost</div>
                                                <h5 className="font-weight-bold text-dark mb-0">{formatExactCurrency(floor.totalCost)}</h5>
                                            </div>
                                        </div>

                                        <div className="table-responsive">
                                            <Table hover className="custom-detail-table">
                                                <thead>
                                                    <tr>
                                                        <th className="sticky-col">Module</th>
                                                        <th className="text-center">Items</th>
                                                        <th className="text-right">Cost</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {floor.modules.map((module, moduleIdx) => (
                                                        <tr key={moduleIdx}>
                                                            <td className="font-weight-bold text-dark sticky-col">
                                                                <span className="status-badge status-valid capitalize">
                                                                    {module.displayName}
                                                                </span>
                                                            </td>
                                                            <td className="text-center text-dark">
                                                                {module.items.length}
                                                            </td>
                                                            <td className="text-right font-weight-bold text-dark">
                                                                {formatExactCurrency(module.totalCost)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot>
                                                    <tr className="bg-light font-weight-bold">
                                                        <th className="sticky-col">Floor Total</th>
                                                        <th className="text-center">{floor.modules.reduce((sum, m) => sum + m.items.length, 0)}</th>
                                                        <th className="text-right">{formatExactCurrency(floor.totalCost)}</th>
                                                    </tr>
                                                </tfoot>
                                            </Table>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        ) : (
                            <Card className="activity-card p-4">
                                <div className="text-center py-5">
                                    <i className="fa fa-cubes fa-3x text-muted mb-3"></i>
                                    <h5>No Module Cost Data Available</h5>
                                    <p className="text-muted text-sm">
                                        Module-wise cost data will appear here once estimation is generated.
                                    </p>
                                </div>
                            </Card>
                        )}
                    </div>
                )}

{activeTab === "material_cost" && (
                    <div>
                        {materialFloorData && materialFloorData.length > 0 ? (
                            <div>
                                {/* Material Cost Header */}
                                <Card className="activity-card p-4 mb-4">
                                    <div className="d-flex align-items-center justify-content-between mb-3 tab-header-responsive">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="p-2 rounded bg-light-green text-green">
                                                <i className="fa fa-cubes fa-lg"></i>
                                            </div>
                                            <div>
                                                <h5 className="font-weight-bold mb-0">Material Cost Summary</h5>
                                                <span className="text-muted text-sm">Floor-wise material breakdown with module details</span>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="text-right">
                                                <div className="text-muted" style={{ fontSize: '0.875rem' }}>Total Materials Cost</div>
                                                <h4 className="font-weight-bold text-gradient mb-0">
                                                    {(() => {
                                                        const grandTotal = materialFloorData.reduce((sum, floor) => {
                                                            return sum + floor.modules.reduce((floorSum, module) => {
                                                                return floorSum + module.materials.reduce((modSum, m) => {
                                                                    const rate = getRate(materialRates, m.material_name);
                                                                    if (rate != null && rate !== 0 && m.quantity != null) {
                                                                        return modSum + (m.quantity * rate);
                                                                    }
                                                                    return modSum;
                                                                }, 0);
                                                            }, 0);
                                                        }, 0);
return grandTotal > 0 ? `₹${grandTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '-';
                                                })()}
                                            </h4>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                            {/* Floor Cards */}
{materialFloorData.map((floor, floorIdx) => {
                                        const isFloorSelected = selectedMaterialFloor === floor.name;
                                        const selectedModule = isFloorSelected ? floor.modules.find(m => m.key === selectedMaterialModule) : null;
                                        const showModuleTotal = isFloorSelected && selectedModule;
                                        
                                        const floorTotalAmount = floor.modules.reduce((floorSum, module) => {
                                            return floorSum + module.materials.reduce((modSum, m) => {
                                                const rate = getRate(materialRates, m.material_name);
                                                if (rate != null && rate !== 0 && m.quantity != null) {
                                                    return modSum + (m.quantity * rate);
                                                }
                                                return modSum;
                                            }, 0);
                                        }, 0);
                                        
                                        const moduleTotalAmount = showModuleTotal ? selectedModule.materials.reduce((modSum, m) => {
                                            const rate = getRate(materialRates, m.material_name);
                                            if (rate != null && rate !== 0 && m.quantity != null) {
                                                return modSum + (m.quantity * rate);
                                            }
                                            return modSum;
                                        }, 0) : 0;
                                        
                                        const displayTotal = showModuleTotal ? moduleTotalAmount : floorTotalAmount;
                                        const displayLabel = showModuleTotal ? `${selectedModule.displayName} Total` : 'Total Amount';

                                        return (
                                            <Card key={floorIdx} className="activity-card p-4 mb-4">
                                                <div className="d-flex align-items-center justify-content-between mb-3 tab-header-responsive">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div className="p-2 rounded bg-light-blue text-blue">
                                                            <i className="fa fa-layer-group fa-lg"></i>
                                                        </div>
                                                        <div>
                                                            <h5 className="font-weight-bold mb-0">{floor.name}</h5>
                                                            <span className="text-muted text-sm">{floor.modules.length} modules</span>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-muted text-xs">{displayLabel}</div>
                                                        <h5 className="font-weight-bold text-dark mb-0">
                                                            {displayTotal > 0 ? `₹${displayTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '-'}
                                                        </h5>
                                                    </div>
                                                </div>
                                                {/* Module Badges */}
                                                <div className="d-flex flex-wrap gap-2">
                                                    {floor.modules.map((module, moduleIdx) => (
                                                        <span
                                                            key={moduleIdx}
                                                            className={`status-badge capitalize px-3 py-2 $
                                                                selectedMaterialFloor === floor.name && selectedMaterialModule === module.key
                                                                    ? 'status-inprogress'
                                                                    : 'status-valid'
                                                            `}
                                                            style={{
                                                                fontSize: "0.9rem",
                                                                cursor: "pointer",
                                                                transition: "all 0.2s ease",
                                                                border:
                                                                    selectedMaterialFloor === floor.name && selectedMaterialModule === module.key
                                                                        ? "2px solid #7e22ce"
                                                                        : "none"
                                                            }}
                                                            onClick={() => handleMaterialModuleClick(floor, module)}
                                                            onMouseEnter={(e) => {
                                                                if (!(selectedMaterialFloor === floor.name && selectedMaterialModule === module.key)) {
                                                                    e.currentTarget.style.transform = "translateY(-2px)";
                                                                    e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1)";
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.transform = "translateY(0)";
                                                                e.currentTarget.style.boxShadow = "none";
                                                            }}
                                                        >
                                                            <i className={`fa $
                                                                selectedMaterialFloor === floor.name && selectedMaterialModule === module.key
                                                                    ? 'fa-eye'
                                                                    : 'fa-cube'
                                                            } mr-2`}></i>
                                                            {module.displayName}
                                                            <span className="ms-2 text-xs">({module.materials.length})</span>
                                                        </span>
                                                    ))}
                                                </div>
                                            </Card>
                                        );
                                    })}
                                {/* Material Table for Selected Module */}
                                {selectedMaterialModuleData && (
                                    <Card className="activity-card p-4 mb-4">
                                        <div className="d-flex align-items-center gap-2 mb-4">
                                            <div className="p-2 rounded bg-light-green text-green">
                                                <i className="fa fa-cubes fa-lg"></i>
                                            </div>
                                            <div>
                                                <h5 className="font-weight-bold mb-0">
                                                    {selectedMaterialModuleData.floorName} - {selectedMaterialModuleData.moduleName}
                                                </h5>
                                                <span className="text-muted text-sm">
                                                    {selectedMaterialModuleData.materials.length} materials
                                                </span>
</div>
                                        </div>

<div className="table-responsive">
                                            <Table hover className="custom-detail-table">
                                                <thead>
                                                    <tr>
                                                        <th className="sticky-col">Material</th>
                                                        <th className="text-center">Unit</th>
                                                        <th className="text-right">Quantity</th>
                                                        <th className="text-right">Rate (₹)</th>
                                                        <th className="text-right">Amount (₹)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedMaterialModuleData.materials.map((material, matIdx) => {
                                                        const rate = getRate(materialRates, material.material_name);
                                                        const unit = getRateUnit(materialRates, material.material_name);
                                                        return (
                                                            <tr key={matIdx}>
                                                                <td className="font-weight-bold text-dark sticky-col">
                                                                    {material.material_name}
                                                                </td>
                                                                <td className="text-center">
                                                                    <span className="badge bg-secondary">
                                                                        {unit !== "-" ? unit : material.unit}
                                                                    </span>
                                                                </td>
                                                                <td className="text-right text-dark">
                                                                    {material.quantity != null
                                                                        ? material.quantity.toLocaleString()
                                                                        : '-'}
                                                                </td>
                                                                <td className="text-right text-muted">
                                                                    {(() => {
                                                                        if (rate != null && rate !== 0) {
                                                                            return formatExactCurrency(rate);
                                                                        }
                                                                        return materialRatesLoading ? (
                                                                            <span className="text-muted">
                                                                                <i className="fa fa-spinner fa-spin mr-1"></i>
                                                                                Loading...
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-info">
                                                                                <i className="fa fa-info-circle mr-1"></i>
                                                                                Rate not available
                                                                            </span>
                                                                        );
                                                                    })()}
                                                                </td>
                                                                <td className="text-right text-muted">
                                                                    {(() => {
                                                                        if (rate != null && rate !== 0 && material.quantity != null) {
                                                                            const amount = material.quantity * rate;
                                                                            return `₹${amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
                                                                        }
                                                                        return materialRatesLoading ? (
                                                                            <span className="text-muted">
                                                                                <i className="fa fa-spinner fa-spin mr-1"></i>
                                                                                Loading...
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-info">
                                                                                <i className="fa fa-info-circle mr-1"></i>
                                                                                Amount not available
                                                                            </span>
                                                                        );
                                                                    })()}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                                <tfoot>
                                                    <tr className="bg-light font-weight-bold">
                                                        <th className="sticky-col" colSpan="3">Module Total</th>
                                                        <th className="text-right">
                                                           
                                                        </th>
                                                        <th className="text-right">
                                                            {(() => {
                                                                const totalAmount = selectedMaterialModuleData.materials.reduce((sum, m) => {
                                                                    const rate = getRate(materialRates, m.material_name);
                                                                    if (rate != null && rate !== 0 && m.quantity != null) {
                                                                        return sum + (m.quantity * rate);
                                                                    }
                                                                    return sum;
                                                                }, 0);
                                                                return totalAmount > 0 ? `₹${totalAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '-';
                                                            })()}
                                                        </th>
                                                        
                                                    </tr>
                                                </tfoot>
                                            </Table>
                                        </div>
                                         
                                    </Card>
                                )}
                                 
                            </div>
                        ) : (
                            <Card className="activity-card p-4">
                                <div className="text-center py-5">
                                    <i className="fa fa-cubes fa-3x text-muted mb-3"></i>
                                    <h5>No Material Cost Data Available</h5>
                                    <p className="text-muted text-sm">
                                        Material cost summary will appear here once generated.
                                    </p>
                                </div>
                            </Card>
                        )}
                    </div>
                )}
{activeTab === "labour_cost" && (
                    <div>
                        {labourFloorData && labourFloorData.length > 0 ? (
                            <div>
                                {/* Labour Cost Header */}
                                <Card className="activity-card p-4 mb-4">
                                    <div className="d-flex align-items-center justify-content-between mb-3 tab-header-responsive">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="p-2 rounded bg-light-orange text-orange">
                                                <i className="fa fa-users fa-lg"></i>
                                            </div>
                                            <div>
                                                <h5 className="font-weight-bold mb-0">Labour Cost Summary</h5>
                                                <span className="text-muted text-sm">Floor-wise labour breakdown with module details</span>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="text-right">
                                                <div className="text-muted" style={{ fontSize: '0.875rem' }}>Total Labour Cost</div>
                                                <h4 className="font-weight-bold text-gradient mb-0">
                                                    {(() => {
                                                        const grandTotal = labourFloorData.reduce((sum, floor) => {
                                                            return sum + floor.modules.reduce((floorSum, module) => {
                                                                return floorSum + module.labourCategories.reduce((modSum, l) => {
                                                                    const rate = getRate(labourRates, l.labour_category) || 1;
                                                                    const roundedQty = Math.round(l.quantity);
                                                                    return modSum + (roundedQty * rate);
                                                                }, 0);
                                                            }, 0);
                                                        }, 0);
return grandTotal > 0 ? `₹${grandTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '-';
                                                    })()}
                                                </h4>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                                {/* Floor Cards */}
                                {labourFloorData.map((floor, floorIdx) => {
                                    const isFloorSelected = selectedLabourFloor === floor.name;
                                    const selectedModule = isFloorSelected ? floor.modules.find(m => m.key === selectedLabourModule) : null;
                                    const showModuleTotal = isFloorSelected && selectedModule;
                                    
                                    const floorTotalAmount = floor.modules.reduce((floorSum, module) => {
                                        return floorSum + module.labourCategories.reduce((modSum, l) => {
                                            const rate = getRate(labourRates, l.labour_category) || 1;
                                            const roundedQty = Math.round(l.quantity);
                                            return modSum + (roundedQty * rate);
                                        }, 0);
                                    }, 0);
                                    
                                    const moduleTotalAmount = showModuleTotal ? selectedModule.labourCategories.reduce((sum, l) => {
                                        const rate = getRate(labourRates, l.labour_category) || 1;
                                        const roundedQty = Math.round(l.quantity);
                                        return sum + (roundedQty * rate);
                                    }, 0) : 0;
                                    const displayTotal = showModuleTotal ? moduleTotalAmount : floorTotalAmount;
                                    const displayLabel = showModuleTotal ? `${selectedModule.displayName} Total` : 'Total Amount';

                                    return (
                                        <Card key={floorIdx} className="activity-card p-4 mb-4">
                                            <div className="d-flex align-items-center justify-content-between mb-3 tab-header-responsive">
                                                <div className="d-flex align-items-center gap-2">
                                                    <div className="p-2 rounded bg-light-blue text-blue">
                                                        <i className="fa fa-layer-group fa-lg"></i>
                                                    </div>
                                                    <div>
                                                        <h5 className="font-weight-bold mb-0">{floor.name}</h5>
                                                        <span className="text-muted text-sm">{floor.modules.length} modules</span>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-muted text-xs">{displayLabel}</div>
                                                    <h5 className="font-weight-bold text-dark mb-0">
                                                        {displayTotal > 0 ? `₹${displayTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '-'}
                                                    </h5>
                                                </div>
                                            </div>

                                            {/* Module Badges */}
                                            <div className="d-flex flex-wrap gap-2">
                                                {floor.modules.map((module, moduleIdx) => (
                                                    <span
                                                        key={moduleIdx}
                                                        className={`status-badge capitalize px-3 py-2 ${
                                                            selectedLabourFloor === floor.name && selectedLabourModule === module.key
                                                                ? 'status-inprogress'
                                                                : 'status-valid'
                                                        }`}
                                                        style={{
                                                            fontSize: "0.9rem",
                                                            cursor: "pointer",
                                                            transition: "all 0.2s ease",
                                                            border:
                                                                selectedLabourFloor === floor.name && selectedLabourModule === module.key
                                                                    ? "2px solid #7e22ce"
                                                                    : "none"
                                                        }}
                                                        onClick={() => handleLabourModuleClick(floor, module)}
                                                    >
                                                        <i className={`fa ${selectedLabourFloor === floor.name && selectedLabourModule === module.key ? 'fa-eye' : 'fa-users'} mr-2`}></i>
                                                        {module.displayName}
                                                        <span className="ms-2 text-xs">({module.labourCategories.length})</span>
                                                    </span>
                                                ))}
                                            </div>
                                        </Card>
                                    );
                                })}

                                {/* Labour Module Detail Card */}
                                {selectedLabourModuleData && (
                                    <Card className="activity-card p-4 mb-4">
                                        <div className="d-flex align-items-center gap-2 mb-4">
                                            <div className="p-2 rounded bg-light-orange text-orange">
                                                <i className="fa fa-users fa-lg"></i>
                                            </div>
                                            <div>
                                                <h5 className="font-weight-bold mb-0">
                                                    {selectedLabourModuleData.floorName} - {selectedLabourModuleData.moduleName}
                                                </h5>
                                                <span className="text-muted text-sm">
                                                    {selectedLabourModuleData.labourCategories.length} labour categories
                                                </span>
                                            </div>
                                        </div>

                                        <div className="table-responsive">
                                            <Table hover className="custom-detail-table">
                                                <thead>
                                                    <tr>
                                                        <th className="sticky-col">Labour Category</th>
                                                        <th className="text-center">Unit</th>
                                                        <th className="text-right">Quantity</th>
                                                        <th className="text-right">Rate (₹)</th>
                                                        <th className="text-right">Amount (₹)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedLabourModuleData.labourCategories.map((labour, idx) => {
                                                        const rate = getRate(labourRates, labour.labour_category) || 1;
                                                        const roundedQty = Math.round(labour.quantity);
                                                        const amount = roundedQty * rate;
                                                        return (
                                                            <tr key={idx}>
                                                                <td className="font-weight-bold text-dark sticky-col">{labour.labour_category}</td>
                                                                <td className="text-center">
                                                                    <span className="badge bg-secondary">{labour.unit}</span>
                                                                </td>
                                                                <td className="text-right text-dark">
                                                                    {labour.quantity != null ? Math.round(labour.quantity).toLocaleString() : '-'}
                                                                </td>
                                                                <td className="text-right text-muted">
                                                                    {rate != null && rate !== 0 ? formatExactCurrency(rate) : (
                                                                        labourRatesLoading ? (
                                                                            <span className="text-muted">
                                                                                <i className="fa fa-spinner fa-spin mr-1"></i>
                                                                                Loading...
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-info">
                                                                                <i className="fa fa-info-circle mr-1"></i>
                                                                                Rate not available
                                                                            </span>
                                                                        )
                                                                    )}
                                                                </td>
                                                                <td className="text-right font-weight-bold text-dark">
                                                                    {formatExactCurrency(amount)}
                                                                </td>
                                                            </tr>
                                                        )
                                                    })}
                                                </tbody>
                                                <tfoot>
                                                    <tr className="bg-light font-weight-bold">
                                                        <th className="sticky-col" colSpan="4">Module Total</th>
                                                        <th className="text-right">
                                                            {formatExactCurrency(selectedLabourModuleData.labourCategories.reduce((sum, l) => {
                                                                const rate = getRate(labourRates, l.labour_category) || 1;
                                                                const roundedQty = Math.round(l.quantity);
                                                                return sum + (roundedQty * rate);
                                                            }, 0))}
                                                        </th>
                                                    </tr>
                                                </tfoot>
                                            </Table>
                                        </div>
                                    </Card>
                                )}
                            </div>
                        ) : (
                            <Card className="activity-card p-4">
                                <div className="text-center py-5">
                                    <i className="fa fa-users fa-3x text-muted mb-3"></i>
                                    <h5>No Labour Cost Data Available</h5>
                                    <p className="text-muted text-sm">
                                        Labour cost summary will appear here once estimation is generated.
                                    </p>
                                </div>
                            </Card>
                        )}
                    </div>
                )}
</div>
{/* Project Settings Modal (Tabbed: Module Rates | Material Rates | Labour Rates) */}
            <CustomCostModal
                show={showSettingsModal}
                onHide={handleCloseSettings}
                activeTab={settingsActiveTab}
                setActiveTab={setSettingsActiveTab}
                formatCurrency={formatCurrency}
                formatExactCurrency={formatExactCurrency}
                onSaveAll={async () => {
                    // Save material and labour rates first (they trigger recomputation)
                    // Then save module rates last so manual edits override auto-calculated values
                    await handleSaveMaterialSettings();
                    await handleSaveLabourSettings();
                    await handleSaveSettings();
                    handleCloseSettings();
                }}
                onClose={handleCloseSettings}
                showToast={showToast}
                tabs={{
                    module: {
                        config: {
                            title: "Project Settings - Configure Module Rates",
                            icon: "fa-cogs",
                            itemLabel: "Item",
                            emptyMessage: "Loading cost data...",
                            getFloors: (rows) => {
                                if (!rows) return [];
                                const floorMap = new Map();
                                let currentFloor = '';
                                let currentModule = '';
                                rows.forEach(row => {
                                    if (row.type === 'floor') {
                                        currentFloor = row['Floor / Module'] || '';
                                        if (currentFloor && !floorMap.has(currentFloor)) {
                                            floorMap.set(currentFloor, { modules: new Map(), totalAmount: 0 });
                                        }
                                    } else if (row.type === 'module' && currentFloor) {
                                        currentModule = row['Floor / Module'] || '';
                                        const floor = floorMap.get(currentFloor);
                                        if (floor && currentModule && !floor.modules.has(currentModule)) {
                                            floor.modules.set(currentModule, { items: [], totalAmount: 0 });
                                        }
                                    } else if (row.type === 'item' && currentFloor && currentModule) {
                                        const floor = floorMap.get(currentFloor);
                                        if (floor) {
                                            const module = floor.modules.get(currentModule);
                                            if (module) {
                                                const customRate = settingsCustomRates[row.Item];
                                                const rate = customRate !== undefined && customRate !== null
                                                    ? Number(customRate)
                                                    : (Number(row['Rate (₹)']) || 0);
                                                const qty = Number(row.Quantity) || 0;
                                                const amount = qty * rate;
                                                module.items.push({
                                                    item: row.Item,
                                                    description: row.Description || row.Item,
                                                    quantity: qty,
                                                    unit: row.Unit || '-',
                                                    originalRate: Number(row['Rate (₹)']) || 0,
                                                    customRate: customRate !== undefined && customRate !== null ? Number(customRate) : null,
                                                    rate: rate,
                                                    amount: amount,
                                                    hasCustomRate: customRate !== undefined && customRate !== null
                                                });
                                                module.totalAmount += amount;
                                                floor.totalAmount += amount;
                                            }
                                        }
                                    }
                                });
                                return Array.from(floorMap.entries()).map(([floorName, floorData]) => ({
                                    name: floorName,
                                    modules: Array.from(floorData.modules.entries()).map(([moduleName, moduleData]) => ({
                                        displayName: moduleName,
                                        items: moduleData.items,
                                        totalAmount: moduleData.totalAmount
                                    })),
                                    totalAmount: floorData.totalAmount
                                }));
                            },
                            getModules: (floor) => floor.modules,
                            getModuleItems: (module) => module.items,
                            getItemName: (item) => item.item,
                            getItemQty: (item) => item.quantity,
                            getItemUnit: (item) => item.unit,
                            getCurrentRate: (item) => item.originalRate,
                            getCustomRate: (item) => item.customRate,
                            getAmount: (item) => item.amount,
                            hasCustomRate: (item) => item.hasCustomRate,
                            getGrandTotal: (summary) => summary.rows
                                .filter(r => r.type === 'grand_total')
                                .reduce((sum, r) => sum + (Number(r['Amount (₹)']) || 0), 0)
                        },
                        data: cost?.rows,
                        customSummary: settingsCustomCostSummary,
                        rates: null,
                        customRates: settingsCustomRates,
                        rateInputStrings: settingsRateInputStrings,
                        expandedFloor,
                        setExpandedFloor,
                        error: settingsError,
                        setError: setSettingsError,
                        saving: settingsSaving,
                        handleRateChange: handleSettingsRateChange,
                        handleSave: handleSaveSettings,
                    },
                    material: {
                        config: {
                            title: "Material Settings - Configure Material Rates",
                            icon: "fa-cubes",
                            itemLabel: "Material",
                            emptyMessage: "Loading material data...",
                            getFloors: (data, rates, customRates) => {
                                if (!data || data.length === 0) return [];
                                return data.map(floor => {
                                    const updatedModules = floor.modules.map(module => {
                                        const updatedMaterials = module.materials.map(material => {
                                            const customRate = customRates[material.material_name];
                                            const rate = customRate !== undefined && customRate !== null
                                                ? Number(customRate)
                                                : getRate(rates, material.material_name, DEFAULT_MATERIAL_RATES);
                                            return {
                                                ...material,
                                                rate: rate,
                                                amount: material.quantity * rate
                                            };
                                        });
                                        const moduleTotalAmount = updatedMaterials.reduce((sum, m) => sum + (m.amount || 0), 0);
                                        return {
                                            ...module,
                                            materials: updatedMaterials,
                                            totalAmount: moduleTotalAmount
                                        };
                                    });
                                    const floorTotalAmount = updatedModules.reduce((sum, m) => sum + (m.totalAmount || 0), 0);
                                    return {
                                        ...floor,
                                        modules: updatedModules,
                                        totalAmount: floorTotalAmount
                                    };
                                });
                            },
                            getModules: (floor) => floor.modules,
                            getModuleItems: (module) => module.materials,
                            getItemName: (m) => m.material_name,
                            getItemQty: (m) => m.quantity,
                            getItemUnit: (m) => m.unit,
                            getCurrentRate: (m, rates) => getRate(rates, m.material_name, DEFAULT_MATERIAL_RATES),
                            getCustomRate: (m, rates, customRates) => customRates[m.material_name],
                            getAmount: (m, rates, customRates) => {
                                const customRate = customRates[m.material_name];
                                const rate = customRate !== undefined && customRate !== null
                                    ? Number(customRate)
                                    : getRate(rates, m.material_name, DEFAULT_MATERIAL_RATES);
                                return m.quantity * rate;
                            },
                            hasCustomRate: (m, rates, customRates) => customRates[m.material_name] !== undefined && customRates[m.material_name] !== getRate(rates, m.material_name, DEFAULT_MATERIAL_RATES),
                            getGrandTotal: (summary) => summary.reduce((sum, floor) => sum + (floor.totalAmount || 0), 0)
                        },
                        data: materialCustomSummary || materialFloorData,
                        customSummary: materialCustomSummary,
                        rates: materialRates,
                        customRates: materialCustomRates,
                        rateInputStrings: materialRateInputStrings,
                        expandedFloor: expandedMaterialFloor,
                        setExpandedFloor: setExpandedMaterialFloor,
                        error: materialSettingsError,
                        setError: setMaterialSettingsError,
                        saving: materialSettingsSaving,
                        handleRateChange: handleMaterialRateChange,
                        handleSave: handleSaveMaterialSettings,
                    },
                    labour: {
                        config: {
                            title: "Labour Settings - Configure Labour Rates",
                            icon: "fa-users",
                            itemLabel: "Labour Category",
                            emptyMessage: "Loading labour data...",
                            getFloors: (data, rates, customRates) => {
                                if (!data || data.length === 0) return [];
                                return data.map(floor => {
                                    const updatedModules = floor.modules.map(module => {
                                        const updatedLabourCategories = module.labourCategories.map(labour => {
                                            const customRate = customRates[labour.labour_category];
                                            const rate = customRate !== undefined && customRate !== null
                                                ? Number(customRate)
                                                : (rates[labour.labour_category]?.rate || 0);
                                            const roundedQty = Math.round(labour.quantity);
                                            return {
                                                ...labour,
                                                rate: rate,
                                                amount: roundedQty * rate
                                            };
                                        });
                                        const moduleTotalAmount = updatedLabourCategories.reduce((sum, l) => sum + (l.amount || 0), 0);
                                        return {
                                            ...module,
                                            labourCategories: updatedLabourCategories,
                                            totalAmount: moduleTotalAmount
                                        };
                                    });
                                    const floorTotalAmount = updatedModules.reduce((sum, m) => sum + (m.totalAmount || 0), 0);
                                    return {
                                        ...floor,
                                        modules: updatedModules,
                                        totalAmount: floorTotalAmount
                                    };
                                });
                            },
                            getModules: (floor) => floor.modules,
                            getModuleItems: (module) => module.labourCategories,
                            getItemName: (l) => l.labour_category,
                            getItemQty: (l) => Math.round(l.quantity),
                            getItemUnit: (l) => l.unit,
                            getCurrentRate: (l, rates) => rates[l.labour_category]?.rate || 0,
                            getCustomRate: (l, rates, customRates) => customRates[l.labour_category],
                            getAmount: (l, rates, customRates) => {
                                const customRate = customRates[l.labour_category];
                                const rate = customRate !== undefined && customRate !== null
                                    ? Number(customRate)
                                    : (rates[l.labour_category]?.rate || 0);
                                return Math.round(l.quantity) * rate;
                            },
                            hasCustomRate: (l, rates, customRates) => customRates[l.labour_category] !== undefined && customRates[l.labour_category] !== (rates[l.labour_category]?.rate || 0),
                            getGrandTotal: (summary) => summary.reduce((sum, floor) => sum + (floor.totalAmount || 0), 0)
                        },
                        data: labourCustomSummary || labourFloorData,
                        customSummary: labourCustomSummary,
                        rates: labourRates,
                        customRates: labourCustomRates,
                        rateInputStrings: labourRateInputStrings,
                        expandedFloor: expandedLabourFloor,
                        setExpandedFloor: setExpandedLabourFloor,
                        error: labourSettingsError,
                        setError: setLabourSettingsError,
                        saving: labourSettingsSaving,
                        handleRateChange: handleLabourRateChange,
                        handleSave: handleSaveLabourSettings,
                    }
                }}
            />
            <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
               <Toast
  show={toast.show}
  bg={toast.type}
  autohide={true}
  delay={3000}
  onClose={() => setToast({ ...toast, show: false })}
  style={{
    minWidth: "320px",
    maxWidth: "450px",
    border: "none",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.18)",
    position: "fixed",
    top: "20px",
    right: "20px",
    zIndex: 9999,
  }}
>
  <Toast.Body
    className="text-white"
    style={{
      backgroundColor: "#16a34a",
      padding: "14px 18px",
      fontSize: "15px",
      fontWeight: "500",
      lineHeight: "1.5",
      letterSpacing: "0.2px",
    }}
  >
    {toast.message}
  </Toast.Body>
</Toast>
            </ToastContainer>
        </div>
    );
}